import type {
  AntCaste,
  AntSimWorld,
  AntState,
  FoodField,
  NestConfig,
  Pheromones,
  PlayerControl,
} from './contracts';
import { FORAGE_CONFIG } from './contracts';
import type { Terrain } from '../game/Terrain';

export type AntEvent = 'bite' | 'death' | 'pickup' | 'gift' | 'respawn';

interface AntWorldOptions {
  maxSpeed?: number;
  wanderRadius?: number;
  onEvent?: (e: AntEvent) => void;
  /** Per-caste max HP override. When omitted each caste falls back to
   *  the built-in defaults (see hpForCaste). */
  casteHp?: Partial<Record<AntCaste, number>>;
}

interface AntInternal extends AntState {
  targetHeading: number;
  pauseTimer: number;
  nextPauseIn: number;
  /** Scout report state. 'enemy' = sprinting home to alert soldiers of a
   *  spotted threat; 'food' = either walking TO the food (laying a trail
   *  along the way) or sprinting home from it. 'none' otherwise. */
  reportKind: 'none' | 'enemy' | 'food';
  reportTargetX: number;
  reportTargetZ: number;
  /** True once a food-scouting ant has reached the food spot — flips
   *  the second leg of the report (walk to food → sprint home). Unused
   *  for enemy reports (those sprint home immediately, no detour). */
  reportReachedFood: boolean;
  /** Seconds until this scout may report again. Prevents a scout from
   *  oscillating home-out-home on the same sighting every frame. */
  reportCooldown: number;
  /** Follower flag: this ant has committed to attacking the opposing nest
   *  rather than trailing the player. Stays set until the enemy queen is
   *  dead or the invader is killed. */
  invadingEnemyNest: boolean;
  /** Soldier committed to a scout-reported alarm trail. Stays latched (and
   *  the ant keeps marching) even after the scout-alarm TTL expires — they
   *  finish the trail, patrol briefly, then return to the rim. */
  alarmCommitted: boolean;
  alarmX: number;
  alarmZ: number;
  /** Seconds of on-site patrol remaining once the soldier has arrived at
   *  the alarm spot. Counts down to 0, then `alarmCommitted` clears and
   *  the ant goes back to its rim slot. */
  alarmWanderT: number;
  /** Seconds until this soldier breaks rank for a walkabout. Reset to a
   *  fresh 2–5 min interval after each walkabout ends. */
  breakRankT: number;
  /** Seconds remaining in the current break-rank walkabout. While > 0 the
   *  soldier wanders outside its rim slot instead of standing guard. */
  breakRankActiveT: number;
  /** Currently-assigned soldier job. Re-rolled every few minutes so the
   *  colony rebalances between entrance guarding, perimeter patrol, and
   *  forager escort duty. Unused for non-soldier castes. */
  soldierRole: 'guard' | 'patrol' | 'escort';
  /** Seconds until this soldier re-rolls its role. */
  soldierRoleT: number;
  /** Current wander micro-target for the guard role (picked once, walked
   *  toward, re-rolled on arrival). Stored in world space. */
  guardTargetX: number;
  guardTargetZ: number;
  /** Seconds of elapsed life since spawn. Drives the per-caste lifespan
   *  decline — HP starts fraying after 70% of the caste's natural lifespan
   *  and the ant dies of old age at 100%. Player is exempt (see ageTick). */
  age: number;
  /** Pebble index currently carried in this ant's jaws. -1 if none. Held
   *  pebbles ride visually on the ant via PebbleMesh.update. */
  heldPebble: number;
  /** Player-only. Pebble index to pick up when the current goal is
   *  reached. -1 = no pending pickup. Cleared on arrival or on any new
   *  goal set via setPlayerGoal. */
  pickupOnArrive: number;
  /** Player-only. If true, drop whatever's held when the current goal is
   *  reached. Cleared on arrival or on any new goal set via
   *  setPlayerGoal. */
  dropOnArrive: boolean;
  /** Other ant currently in trophalaxis with this one. -1 = none.
   *  Set on both sides when giftFlashT fires; the renderer uses this to
   *  orient both partners face-to-face during the feed window. */
  giftPartnerId: number;
  /** True while this ant is rallying with the player's pebble parade:
   *  grab a pebble, follow the player while carrying, drop when the
   *  player drops. Restricted to same-team workers. Cleared on drop or
   *  on player death. */
  pebbleFollower: boolean;
  /** Sub-state for pebbleFollower ants:
   *    'pickup' — walking to a free pebble on the ground
   *    'follow' — carrying a pebble, following the player
   *    'drop'   — player dropped; walk to drop spot, drop, demote */
  frenzyPhase: 'pickup' | 'follow' | 'drop';
  frenzyTargetX: number;
  frenzyTargetZ: number;
}

const TAU = Math.PI * 2;
const TRAIL_THRESHOLD = 0.05;
const HOME_GRADIENT_BLEND = 0.25;

const FOLLOWER_SEPARATION_RADIUS = 0.4;
const FOLLOWER_CLOSE_RADIUS = 0.5;
const PLAYER_SPEED_MULTIPLIER = 1.5;
const PLAYER_ARRIVAL_RADIUS = 0.08;

const ANT_BODY_RADIUS = 0.05;          // for obstacle collision padding

// Ants are ~7 cm long; tighten the damage range to body-on-body so the
// bite animation actually reads as contact instead of jousting at arm's length.
const MELEE_RADIUS = 0.1;              // enemies within this damage each other
const MELEE_RADIUS_SQ = MELEE_RADIUS * MELEE_RADIUS;
const DMG_PER_ATTACKER_PER_SEC = 0.45; // 1v1 ≈ 2.2s kill; 3v1 ≈ 0.75s
const ENEMY_SENSE = 3;                 // foragers within this chase any visible enemy
const ENEMY_SENSE_SQ = ENEMY_SENSE * ENEMY_SENSE;
const ALLY_FIGHT_SENSE = 5;            // foragers within this chase an ally's fight
const ALLY_FIGHT_SENSE_SQ = ALLY_FIGHT_SENSE * ALLY_FIGHT_SENSE;

const HUNGER_STARVING = 0.7;           // above this, ants ignore combat and focus on food
const GIFT_RADIUS = 0.25;
const GIFT_RADIUS_SQ = GIFT_RADIUS * GIFT_RADIUS;
const GIFT_COOLDOWN = 5;
const GIFT_DONOR_COST = 0.35;
const GIFT_DONOR_CAP = 0.9;
const GIFT_WELL_FED = 0.3;             // donor must be below this
const GIFT_HUNGRY = 0.5;                // recipient must be above this
const GIFT_FLASH_DURATION = 0.8;

const PLAYER_EAT_RADIUS = 0.25;
const PLAYER_EAT_RADIUS_SQ = PLAYER_EAT_RADIUS * PLAYER_EAT_RADIUS;
const PLAYER_EAT_COOLDOWN = 3;
const PLAYER_EAT_DONOR_HUNGER_CEIL = 0.3;
const PLAYER_EAT_DONOR_AFTER = 0.6;

const PLAYER_RESPAWN_DELAY = 2;
const HUNGER_SPEED_BOOST = 0.6;         // hungry ants up to 60% faster

// Nest-defence alarm: if an enemy is within NEST_ALARM_RADIUS of the nest,
// every same-team ant inside NEST_DEFENSE_RADIUS of the nest charges.
const NEST_ALARM_RADIUS = 6.0;
const NEST_ALARM_RADIUS_SQ = NEST_ALARM_RADIUS * NEST_ALARM_RADIUS;
const NEST_DEFENSE_RADIUS = 10.0;
const NEST_DEFENSE_RADIUS_SQ = NEST_DEFENSE_RADIUS * NEST_DEFENSE_RADIUS;

// Scouts act as long-range eyes: they pick up threats and food from further
// out than rank-and-file workers. On a hit they sprint home to alert the
// colony instead of engaging / picking up themselves.
const SCOUT_DETECT_RADIUS = 8.0;
const SCOUT_DETECT_RADIUS_SQ = SCOUT_DETECT_RADIUS * SCOUT_DETECT_RADIUS;
// Scouts see food further than enemies — that's their whole job, and the
// extra range lets them point the colony at distant piles before workers
// would stumble across them on a wander.
const SCOUT_FOOD_DETECT_RADIUS = 14.0;
const SCOUT_FOOD_DETECT_RADIUS_SQ = SCOUT_FOOD_DETECT_RADIUS * SCOUT_FOOD_DETECT_RADIUS;
const SCOUT_FOOD_ARRIVAL_R = 0.6;    // "reached" the food
const SCOUT_FOOD_ARRIVAL_R_SQ = SCOUT_FOOD_ARRIVAL_R * SCOUT_FOOD_ARRIVAL_R;
const SCOUT_ALARM_TTL = 20.0;        // soldiers charge a scout-reported spot for this long
const SCOUT_REPORT_COOLDOWN = 10.0;  // after a successful report the scout bumbles back out
const SCOUT_ARRIVAL_RADIUS_MUL = 1.2;// "home" = nest.radius * this

// Once a soldier latches onto a scout-alarm trail they stay committed until
// they reach the spot AND patrol it for this long. Keeps them from
// U-turning halfway to the fight when the TTL expires or the scout moves on.
const SOLDIER_ALARM_ARRIVAL_R = 1.0;
const SOLDIER_ALARM_ARRIVAL_R_SQ = SOLDIER_ALARM_ARRIVAL_R * SOLDIER_ALARM_ARRIVAL_R;
const SOLDIER_ALARM_PATROL_DURATION = 6.0;
const SOLDIER_ALARM_PATROL_RADIUS = 0.8;


// Followers brought within this range of the opposing nest commit to invading
// it — they peel off the player's train, close on the hole, and start chewing
// on the queen / nurses. Their own-team nest-alarm logic still picks them up
// as intruders, so enemy defenders swarm.
const INVASION_TRIGGER_RADIUS = 2.0;
const INVASION_ORBIT_RADIUS = 0.4;
const INVADER_QUEEN_DPS = 0.6;       // per invading follower
const INVADER_NURSE_DPS = 1.0;       // nurses are softer and easier to focus

// Pebble parade — same-team workers within this radius of the player are
// recruited when the player picks up a pebble. They grab a free pebble
// and follow until the player drops, at which point they all drop too.
const FRENZY_SPREAD_RADIUS = 5.0;
const FRENZY_PICK_RADIUS = 0.35;    // "arrived" at a pebble

export class AntWorld implements AntSimWorld, PlayerControl {
  readonly ants: AntInternal[] = [];
  playerId = -1;

  private nextId = 0;
  private elapsed = 0;
  private playerLastFeedT = -Infinity;
  private readonly maxSpeed: number;
  private readonly wanderRadius: number;
  private readonly onEvent?: (e: AntEvent) => void;
  private readonly casteHpOverride: Partial<Record<AntCaste, number>>;

  private readonly turnNoise = 1.4;
  private readonly headingSlew = 4.5;
  private readonly linearAccel = 2.7;
  private readonly pauseMinGap = 3.0;
  private readonly pauseMaxGap = 7.0;
  private readonly pauseMinDur = 0.5;
  private readonly pauseMaxDur = 1.5;

  private readonly gradScratch: [number, number] = [0, 0];
  private obstacles: Array<{ x: number; z: number; radius: number }> = [];
  /** Live spider positions for ant aggro behaviour. Registered by main.ts. */
  private spiderTargets: Array<{ x: number; z: number; alive: boolean }> = [];

  constructor(
    private terrain: Terrain,
    private nests: NestConfig[],
    private food: FoodField,
    private pheromones: Pheromones[],  // one per team — red ants don't read black's trails
    opts: AntWorldOptions = {},
  ) {
    this.maxSpeed = opts.maxSpeed ?? 0.90;
    this.wanderRadius = opts.wanderRadius ?? 38;
    this.onEvent = opts.onEvent;
    this.casteHpOverride = opts.casteHp ?? {};
  }

  /** Caste max HP — per-world override wins over the built-in default. */
  private hpForCasteLocal(c: AntCaste): number {
    const o = this.casteHpOverride[c];
    return typeof o === 'number' ? o : hpForCaste(c);
  }


  /** Register solid-obstacle circles (rocks, etc.) that ants must navigate around. */
  setObstacles(obs: Array<{ x: number; z: number; radius: number }>): void {
    this.obstacles = obs;
  }

  /** Optional callback returning obstacles near (x,z). Used for grass tufts
   *  that can't be brute-forced against 4 k entries per ant per frame. */
  private nearbyObstaclesFn: ((x: number, z: number) => readonly { x: number; z: number; radius: number }[]) | null = null;
  setNearbyObstacles(fn: (x: number, z: number) => readonly { x: number; z: number; radius: number }[]): void {
    this.nearbyObstaclesFn = fn;
  }

  /** Register spider targets so ants can mob a nearby spider as a swarm. */
  setSpiderTargets(s: Array<{ x: number; z: number; alive: boolean }>): void {
    this.spiderTargets = s;
  }

  /** Pebble hooks — registered from main.ts so stepAnt can run the frenzy
   *  loop without importing the Pebbles module directly. */
  private pebblePickupFn: ((x: number, z: number, r: number, holder: number) => number) | null = null;
  private pebbleDropFn: ((idx: number, x: number, z: number) => void) | null = null;
  private pebbleClosestFn: ((x: number, z: number) => number) | null = null;
  private pebblePosFn: ((idx: number) => { x: number; z: number } | null) | null = null;
  setPebbleHooks(fns: {
    pickup: (x: number, z: number, r: number, holder: number) => number;
    drop:   (idx: number, x: number, z: number) => void;
    closest: (x: number, z: number) => number;
    posOf:  (idx: number) => { x: number; z: number } | null;
  }): void {
    this.pebblePickupFn  = fns.pickup;
    this.pebbleDropFn    = fns.drop;
    this.pebbleClosestFn = fns.closest;
    this.pebblePosFn     = fns.posOf;
  }

  /** Walk the player to (x, z) and pick up pebble `idx` on arrival. If the
   *  pebble gets scooped by another ant en route, the action silently fails
   *  — the player still arrives at the target spot. */
  setPlayerPickupGoal(x: number, z: number, idx: number): void {
    const p = this.ants[this.playerId];
    if (!p) return;
    p.goal = { x, z };
    p.pickupOnArrive = idx;
    p.dropOnArrive = false;
  }

  /** Walk the player to (x, z) and drop the held pebble on arrival. No-op
   *  if the player isn't carrying anything when the click fires. */
  setPlayerDropGoal(x: number, z: number): void {
    const p = this.ants[this.playerId];
    if (!p) return;
    p.goal = { x, z };
    p.pickupOnArrive = -1;
    p.dropOnArrive = true;
  }

  /** Recruit same-team workers near (x, z) into a pebble parade. They'll
   *  grab a free pebble and follow the player until the player drops.
   *  Team-restricted so enemy colonies don't join in. */
  private startPebbleRally(x: number, z: number): void {
    const p = this.ants[this.playerId];
    if (!p) return;
    const R = FRENZY_SPREAD_RADIUS;
    const R2 = R * R;
    for (const a of this.ants) {
      if (!a.alive) continue;
      if (a.team !== p.team) continue;            // only player's colony
      if (a.role !== 'normal') continue;
      if (a.casteType !== 'worker') continue;
      if (a.pebbleFollower) continue;             // already in the parade
      const dx = a.x - x;
      const dz = a.z - z;
      if (dx * dx + dz * dz > R2) continue;
      a.pebbleFollower = true;
      a.frenzyPhase = 'pickup';
      a.frenzyTargetX = 0;
      a.frenzyTargetZ = 0;
      a.carrying = false;
    }
  }

  /** Player dropped their pebble — every pebbleFollower walks to the spot
   *  and drops too, then demotes. Workers who hadn't yet picked up just
   *  demote immediately. */
  private endPebbleRally(x: number, z: number): void {
    for (const a of this.ants) {
      if (!a.pebbleFollower) continue;
      if (a.heldPebble === -1) {
        // Wasn't carrying — just leave the parade.
        a.pebbleFollower = false;
        a.frenzyTargetX = 0;
        a.frenzyTargetZ = 0;
        continue;
      }
      a.frenzyPhase = 'drop';
      const ang = Math.random() * TAU;
      const r = Math.random() * 0.6;
      a.frenzyTargetX = x + Math.cos(ang) * r;
      a.frenzyTargetZ = z + Math.sin(ang) * r;
    }
  }

  /** Pushed in from Weather every tick — drives freeze deaths, hunger rate
   *  multiplier, and speed scaling. 20 °C is the neutral baseline. */
  private currentTemperature = 20;
  setTemperature(t: number): void { this.currentTemperature = t; }
  get temperature(): number { return this.currentTemperature; }

  /** World-config gate for freeze-kill behaviour. When false, cold-snap
   *  events still spawn but don't damage ants. */
  private freezeDeath = true;
  setFreezeDeath(on: boolean): void { this.freezeDeath = on; }

  /** Cold-snap gate flipped by the Weather sim. Freeze damage only
   *  applies while this is true AND freezeDeath is on — replaces the
   *  always-on temperature-based check so winters aren't a constant
   *  ducking-in-the-nest grind. */
  private coldSnapActive = false;
  setColdSnapActive(on: boolean): void { this.coldSnapActive = on; }

  /** Heat-wave gate — symmetric to coldSnapActive. While on, surface
   *  ants take heat damage proportional to how hot it is. Player takes
   *  ¼ damage just like during cold snaps. */
  private heatWaveActive = false;
  setHeatWaveActive(on: boolean): void { this.heatWaveActive = on; }

  /** Set whenever the player descends/ascends in NestMode. While true,
   *  the player ant is immune to freeze + heat damage (the sim still
   *  thinks the body is on the surface, but the camera/UI says they're
   *  safely underground in the queen chamber). */
  private playerUnderground = false;
  setPlayerUnderground(on: boolean): void { this.playerUnderground = on; }

  /** Per-team alarm state refreshed each tick. When `active` is true, every
   *  ant of that team within DEFENSE_RADIUS of its nest aggresses on (ax, az). */
  private readonly nestAlarms: Array<{ active: boolean; x: number; z: number }> = [
    { active: false, x: 0, z: 0 },
    { active: false, x: 0, z: 0 },
  ];

  /** Per-team scout-reported-sighting slot. Raised by a scout who runs home
   *  after seeing an enemy; soldiers read this to abandon their rim slot and
   *  charge the reported (x, z) until `ttl` elapses. */
  private readonly scoutAlarms: Array<{ active: boolean; x: number; z: number; ttl: number }> = [
    { active: false, x: 0, z: 0, ttl: 0 },
    { active: false, x: 0, z: 0, ttl: 0 },
  ];

  private tickScoutAlarms(dt: number): void {
    for (const s of this.scoutAlarms) {
      if (s.ttl <= 0) continue;
      s.ttl -= dt;
      if (s.ttl <= 0) {
        s.ttl = 0;
        s.active = false;
      }
    }
  }

  /**
   * If this scout can see a threat (enemy ant / spider / grasshopper) or a
   * food pile within SCOUT_DETECT_RADIUS, latch that position into `a.report*`
   * and flip the scout into reporting mode. Enemy is checked first — under
   * attack, food can wait.
   */
  private scoutScan(a: AntInternal): void {
    let bestD2 = SCOUT_DETECT_RADIUS_SQ;
    let tx = 0, tz = 0;
    let foundEnemy = false;
    // Opposing nest — highest-value find. Report its exact centre so the
    // colony's soldiers march on the nest, not on whichever stray ant the
    // scout happened to spot nearby.
    const enemyNest = this.nests[1 - a.team];
    const ndx = enemyNest.x - a.x;
    const ndz = enemyNest.z - a.z;
    const nd2 = ndx * ndx + ndz * ndz;
    if (nd2 < bestD2) { bestD2 = nd2; tx = enemyNest.x; tz = enemyNest.z; foundEnemy = true; }
    // Opposing ants.
    for (let i = 0; i < this.ants.length; i++) {
      const o = this.ants[i];
      if (!o.alive || o.team === a.team || o.nestCycleT > 0 || o.role === 'player') continue;
      const dx = o.x - a.x;
      const dz = o.z - a.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; tx = o.x; tz = o.z; foundEnemy = true; }
    }
    // Spiders.
    for (let i = 0; i < this.spiderTargets.length; i++) {
      const sp = this.spiderTargets[i];
      if (!sp.alive) continue;
      const dx = sp.x - a.x;
      const dz = sp.z - a.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; tx = sp.x; tz = sp.z; foundEnemy = true; }
    }
    // Grasshoppers.
    for (let i = 0; i < this.grasshopperTargets.length; i++) {
      const gh = this.grasshopperTargets[i];
      if (!gh.alive) continue;
      const dx = gh.x - a.x;
      const dz = gh.z - a.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; tx = gh.x; tz = gh.z; foundEnemy = true; }
    }
    if (foundEnemy) {
      a.reportKind = 'enemy';
      a.reportTargetX = tx;
      a.reportTargetZ = tz;
      return;
    }
    // No threat — look for food. Scouts see food at SCOUT_FOOD_DETECT_RADIUS
    // (wider than enemy range) so distant piles get spotted faster.
    let foodD2 = SCOUT_FOOD_DETECT_RADIUS_SQ;
    let fx = 0, fz = 0;
    let foundFood = false;
    for (let i = 0; i < this.food.items.length; i++) {
      const it = this.food.items[i];
      if (it.amount <= 0) continue;
      const dx = it.x - a.x;
      const dz = it.z - a.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < foodD2) { foodD2 = d2; fx = it.x; fz = it.z; foundFood = true; }
    }
    if (foundFood) {
      a.reportKind = 'food';
      a.reportTargetX = fx;
      a.reportTargetZ = fz;
      a.reportReachedFood = false;  // walk-to-food leg first
    }
  }

  /**
   * Drive a reporting scout. Two paths:
   *
   *   ENEMY — sprint straight home laying an alarm trail (no detour).
   *   FOOD  — walk TO the food first laying a light food trail, then
   *           sprint home from the food spot laying a heavy trail. The
   *           round-trip path is what workers follow back out, so it has
   *           to actually pass through the food location.
   *
   * On arriving home we raise the alarm (enemy) or just let the trail
   * persist (food), then drop back to normal scouting on cooldown.
   */
  private tickScoutReport(a: AntInternal, dt: number): void {
    const nest = this.nests[a.team];

    // Food walk-out leg — head to the food, lay a moderate food trail.
    if (a.reportKind === 'food' && !a.reportReachedFood) {
      const fdx = a.reportTargetX - a.x;
      const fdz = a.reportTargetZ - a.z;
      if (fdx * fdx + fdz * fdz <= SCOUT_FOOD_ARRIVAL_R_SQ) {
        // Reached the food — flip to homebound for the heavy trail.
        a.reportReachedFood = true;
      } else {
        const heading = Math.atan2(fdz, fdx);
        a.heading = heading;
        a.targetHeading = heading;
        const speed = this.maxSpeed * 1.4;
        a.vx = Math.cos(heading) * speed;
        a.vz = Math.sin(heading) * speed;
        a.x += a.vx * dt;
        a.z += a.vz * dt;
        a.y = this.terrain.getHeight(a.x, a.z);
        a.speed = Math.hypot(a.vx, a.vz);
        // Light food trail outbound — workers don't need to follow this
        // direction, but it bridges the home pheromone field toward the
        // food so the back-trail lights up sooner.
        this.pheromones[a.team].deposit(a.x, a.z, 0, FORAGE_CONFIG.depositFoodTrail * 2 * dt);
        return;
      }
    }

    // Homebound leg — sprint to nest, deposit heavy trail.
    const dx = nest.x - a.x;
    const dz = nest.z - a.z;
    const distSq = dx * dx + dz * dz;
    const arrival = nest.radius * SCOUT_ARRIVAL_RADIUS_MUL;
    if (distSq <= arrival * arrival) {
      if (a.reportKind === 'enemy') {
        const slot = this.scoutAlarms[a.team];
        slot.active = true;
        slot.x = a.reportTargetX;
        slot.z = a.reportTargetZ;
        slot.ttl = SCOUT_ALARM_TTL;
      }
      // Head back toward the sighting so the scout visually "leads" the
      // colony out — workers aim for that direction next tick.
      const outHeading = Math.atan2(a.reportTargetZ - a.z, a.reportTargetX - a.x);
      a.heading = outHeading;
      a.targetHeading = outHeading;
      a.reportKind = 'none';
      a.reportReachedFood = false;
      a.reportCooldown = SCOUT_REPORT_COOLDOWN;
      return;
    }
    const heading = Math.atan2(dz, dx);
    a.heading = heading;
    a.targetHeading = heading;
    const sprint = this.maxSpeed * 1.8;
    a.vx = Math.cos(heading) * sprint;
    a.vz = Math.sin(heading) * sprint;
    a.x += a.vx * dt;
    a.z += a.vz * dt;
    a.y = this.terrain.getHeight(a.x, a.z);
    a.speed = Math.hypot(a.vx, a.vz);
    if (a.reportKind === 'enemy') {
      this.pheromones[a.team].deposit(a.x, a.z, 1, 25 * dt);
    } else if (a.reportKind === 'food') {
      // Heavy food trail running food → home. Workers pick this up at
      // home and follow it outbound to the pile.
      this.pheromones[a.team].deposit(a.x, a.z, 0, FORAGE_CONFIG.depositFoodTrail * 6 * dt);
    }
  }

  private computeNestAlarms(): void {
    for (let t = 0; t < this.nests.length; t++) {
      const nest = this.nests[t];
      const team = t as 0 | 1;
      let bestD2 = NEST_ALARM_RADIUS_SQ;
      let bx = 0, bz = 0, found = false;
      // Opposing-team ants near the nest.
      for (let i = 0; i < this.ants.length; i++) {
        const a = this.ants[i];
        if (!a.alive || a.team === team || a.nestCycleT > 0) continue;
        const dx = a.x - nest.x;
        const dz = a.z - nest.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < bestD2) { bestD2 = d2; bx = a.x; bz = a.z; found = true; }
      }
      // Any alive spider near the nest.
      for (let si = 0; si < this.spiderTargets.length; si++) {
        const sp = this.spiderTargets[si];
        if (!sp.alive) continue;
        const dx = sp.x - nest.x;
        const dz = sp.z - nest.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < bestD2) { bestD2 = d2; bx = sp.x; bz = sp.z; found = true; }
      }
      const slot = this.nestAlarms[team];
      slot.active = found;
      slot.x = bx;
      slot.z = bz;
      // Leave a steep alarm pheromone spike at the intruder position so the
      // colony's regular scent-steering pulls even far-away workers back to
      // help. Uses the home-trail channel for simplicity — high values feel
      // like "urgent rally here" to returning ants.
      if (found) {
        this.pheromones[team].deposit(bx, bz, 1, 40);
      }
    }
  }

  /** Live grasshoppers that scouts + workers hunt for food. */
  private grasshopperTargets: Array<{ x: number; z: number; alive: boolean }> = [];
  private onGrasshopperBite: ((x: number, z: number) => void) | null = null;

  setGrasshopperTargets(
    hoppers: Array<{ x: number; z: number; alive: boolean }>,
    onBite: (x: number, z: number) => void,
  ): void {
    this.grasshopperTargets = hoppers;
    this.onGrasshopperBite = onBite;
  }

  /**
   * Apply area-of-effect damage (hazards). Any alive non-player ant whose
   * horizontal distance from (cx, cz) is below `radius` takes `dps * dt` HP.
   * Player takes half. Returns count of ants killed this call.
   */
  damageArea(cx: number, cz: number, radius: number, dps: number, dt: number): number {
    const r2 = radius * radius;
    const dmg = dps * dt;
    let killed = 0;
    for (let i = 0; i < this.ants.length; i++) {
      const a = this.ants[i];
      if (!a.alive || a.nestCycleT > 1e6) continue; // skip nurses underground
      const dx = a.x - cx;
      const dz = a.z - cz;
      if (dx * dx + dz * dz > r2) continue;
      const take = a.role === 'player' ? dmg * 0.5 : dmg;
      a.hp -= take;
      if (a.hp <= 0 && a.alive) {
        if (a.role === 'player') {
          this.killPlayer(a);
        } else {
          this.killAnt(a);
        }
        killed++;
      }
    }
    return killed;
  }

  /**
   * Push ants directly away from (cx, cz) with a short velocity kick. Used by
   * hazards so ants visibly scatter instead of walking into the danger zone.
   */
  /** Kill every alive ant (including the player) within `radius` of (cx, cz).
   *  Used by the lawnmower — anything under the blade dies instantly. */
  killArea(cx: number, cz: number, radius: number): number {
    const r2 = radius * radius;
    let killed = 0;
    for (let i = 0; i < this.ants.length; i++) {
      const a = this.ants[i];
      if (!a.alive || a.nestCycleT > 1e6) continue;
      const dx = a.x - cx;
      const dz = a.z - cz;
      if (dx * dx + dz * dz > r2) continue;
      if (a.role === 'player') this.killPlayer(a);
      else this.killAnt(a);
      killed++;
    }
    return killed;
  }

  /**
   * Randomly kill `fraction` (0..1) of all currently-alive, above-ground ants
   * regardless of team or caste. Used by the rain event. The player ant is
   * exempt. Underground nurses (nestCycleT > 1e6) are also spared — they're
   * sheltering.
   */
  cullFraction(fraction: number): number {
    const targets: AntInternal[] = [];
    for (let i = 0; i < this.ants.length; i++) {
      const a = this.ants[i];
      if (!a.alive) continue;
      if (a.role === 'player') continue;
      if (a.nestCycleT > 1e6) continue;
      targets.push(a);
    }
    const n = Math.floor(targets.length * Math.max(0, Math.min(1, fraction)));
    // Fisher–Yates partial shuffle to pick n random victims without bias.
    for (let i = 0; i < n; i++) {
      const j = i + Math.floor(Math.random() * (targets.length - i));
      const tmp = targets[i];
      targets[i] = targets[j];
      targets[j] = tmp;
      this.killAnt(targets[i]);
    }
    return n;
  }

  scatterArea(cx: number, cz: number, radius: number, force = 0.8): void {
    const r2 = radius * radius;
    for (let i = 0; i < this.ants.length; i++) {
      const a = this.ants[i];
      if (!a.alive || a.casteType === 'queen' || a.nestCycleT > 1e6) continue;
      const dx = a.x - cx;
      const dz = a.z - cz;
      const d2 = dx * dx + dz * dz;
      if (d2 > r2 || d2 < 1e-6) continue;
      const d = Math.sqrt(d2);
      const falloff = 1 - d / radius;
      a.vx += (dx / d) * force * falloff;
      a.vz += (dz / d) * force * falloff;
      a.targetHeading = Math.atan2(dz, dx);
    }
  }

  get count(): number {
    return this.ants.length;
  }

  spawn(x: number, z: number, team: 0 | 1 = 0, casteType: AntCaste = 'worker'): number {
    const id = this.nextId++;
    const heading = Math.random() * TAU;
    const y = this.terrain.getHeight(x, z);
    // The very first team-0 spawn becomes the player-controlled ant.
    const isFirst = this.playerId === -1 && team === 0;
    const ant: AntInternal = {
      id,
      x,
      y,
      z,
      vx: 0,
      vz: 0,
      heading,
      speed: 0,
      phase: Math.random() * TAU,
      caste: casteType === 'soldier' ? 1 : 0,
      carrying: false,
      task: 'search',
      role: isFirst ? 'player' : 'normal',
      goal: null,
      nestCycleT: 0,
      nestCycleDur: 0,
      team,
      alive: true,
      fightWith: -1,
      fightT: 0,
      deathT: 0,
      hp: isFirst ? 3 : this.hpForCasteLocal(casteType),
      attacking: false,
      hunger: 0,
      hungerRate: isFirst ? 1 / 300 : hungerRateForCaste(casteType),
      lastGiftT: -Infinity,
      giftFlashT: 0,
      giftPartnerId: -1,
      casteType,
      carryValue: 0,
      targetHeading: heading,
      pauseTimer: 0,
      nextPauseIn: this.randRange(this.pauseMinGap, this.pauseMaxGap),
      reportKind: 'none',
      reportTargetX: 0,
      reportTargetZ: 0,
      reportReachedFood: false,
      reportCooldown: 0,
      invadingEnemyNest: false,
      alarmCommitted: false,
      alarmX: 0,
      alarmZ: 0,
      alarmWanderT: 0,
      // Stagger the first break-rank so soldiers don't all walk off at once.
      breakRankT: 60 + Math.random() * 240,
      breakRankActiveT: 0,
      // Initial role distribution via modulo so red + black colonies start
      // with a similar mix. `soldierRoleT` is staggered 60–300 s so they
      // don't all re-roll in sync.
      soldierRole: (() => {
        const r = id % 5;
        return r < 2 ? 'guard' : r < 4 ? 'patrol' : 'escort';
      })(),
      soldierRoleT: 60 + Math.random() * 240,
      guardTargetX: 0,
      guardTargetZ: 0,
      age: 0,
      heldPebble: -1,
      pickupOnArrive: -1,
      dropOnArrive: false,
      pebbleFollower: false,
      frenzyPhase: 'pickup',
      frenzyTargetX: 0,
      frenzyTargetZ: 0,
    };
    this.ants.push(ant);
    if (isFirst) this.playerId = id;
    return id;
  }

  update(dt: number, elapsed: number): void {
    if (dt <= 0) return;
    this.elapsed = elapsed;
    const ants = this.ants;
    // Hunger tick runs first so downstream logic (starving focus, speed) can
    // read the updated value this frame.
    this.hungerTick(dt);
    this.ageTick(dt);
    this.tickRecall(dt);
    // Combat damage tick runs BEFORE stepAnt so steering can react to the
    // `attacking` flag this frame.
    this.meleeDamageTick(dt);
    // Nest-defense alarms: check each nest for intruders before stepping ants
    // so steering can react THIS frame.
    this.computeNestAlarms();
    this.tickScoutAlarms(dt);
    for (let i = 0; i < ants.length; i++) {
      this.stepAnt(ants[i], dt);
    }
    // Post-step: gifting between ants, player eating nearby ants, player respawn.
    this.giftTick();
    this.playerFeedTick();
    this.playerPileEatTick();
    this.playerRespawnTick();
    // Decay the gift-flash timer for the renderer one-shot trigger. Clear
    // giftPartnerId on expiry so the face-to-face override only applies
    // during the flash window.
    for (let i = 0; i < ants.length; i++) {
      const a = ants[i];
      if (a.giftFlashT > 0) {
        a.giftFlashT = Math.max(0, a.giftFlashT - dt);
        if (a.giftFlashT === 0) a.giftPartnerId = -1;
      }
    }
  }

  /** Advance age and apply old-age HP decay. Each caste has a natural
   *  lifespan; for the last 30 % of an ant's life its HP drains linearly so
   *  it dies of old age when the timer reaches 100 %. The player is exempt. */
  private ageTick(dt: number): void {
    const ants = this.ants;
    for (let i = 0; i < ants.length; i++) {
      const a = ants[i];
      if (!a.alive) continue;
      if (a.role === 'player') continue;
      a.age += dt;
      const lifespan = lifespanForCaste(a.casteType);
      if (a.age >= lifespan) {
        // Natural death. killAnt handles corpse drop + event.
        this.killAnt(a);
        continue;
      }
      // Last 30 % of life: drain HP linearly so an ant that never sees
      // combat still winds down to 0 HP at `lifespan`.
      const frailStart = lifespan * 0.7;
      if (a.age > frailStart) {
        const frailSpan = lifespan - frailStart;
        const remaining = lifespan - a.age;
        const maxHp = this.hpForCasteLocal(a.casteType);
        const targetHp = maxHp * (remaining / frailSpan);
        if (a.hp > targetHp) a.hp = targetHp;
      }
    }
  }

  /** Advance hunger; starving ants die (same as combat death). Temperature
   *  modulates hunger: hotter = eat more; also kills any above-ground ant
   *  left out below 0 °C. */
  private hungerTick(dt: number): void {
    const ants = this.ants;
    // Hunger rate mult: 1.0 at 20 °C; doubles by 38 °C.
    const tempHungerMul = Math.max(0.7, 1 + (this.currentTemperature - 20) / 18);
    // Survival-event damage. Cold snap (winter, ~once/year) and heat
    // wave (summer, ~once/year) are explicit short-window events
    // raised by Weather. Outside the events, extreme temperatures just
    // tweak hunger/speed — no HP loss. Player takes ¼ damage during
    // either event so they can sprint to the nest after the warning.
    const freezing = this.freezeDeath && this.coldSnapActive;
    const freezeDps = freezing ? Math.max(0.2, -this.currentTemperature * 0.1) : 0;
    // Heat damage scales with how far above 30 °C we are; at +50 °C
    // workers die in ~5 s, plenty of time to dive after the warning.
    const overheating = this.heatWaveActive;
    const heatDps = overheating ? Math.max(0.2, (this.currentTemperature - 30) * 0.1) : 0;
    for (let i = 0; i < ants.length; i++) {
      const a = ants[i];
      if (!a.alive || a.nestCycleT > 0) continue;
      a.hunger += a.hungerRate * tempHungerMul * dt;
      // Player is exempt while underground in nest mode — they ARE
      // sheltered even though the sim still tracks their body on the
      // surface. Other castes use nestCycleT for the same exemption
      // (see the early-`continue` above).
      const playerSheltered = a.role === 'player' && this.playerUnderground;
      if (freezing && !playerSheltered) {
        const dps = a.role === 'player' ? freezeDps * 0.25 : freezeDps;
        a.hp -= dps * dt;
        if (a.hp <= 0) {
          if (a.role === 'player') this.killPlayer(a);
          else this.killAnt(a);
          continue;
        }
      }
      if (overheating && !playerSheltered) {
        const dps = a.role === 'player' ? heatDps * 0.25 : heatDps;
        a.hp -= dps * dt;
        if (a.hp <= 0) {
          if (a.role === 'player') this.killPlayer(a);
          else this.killAnt(a);
          continue;
        }
      }
      if (a.hunger >= 1.0) {
        a.hunger = 1.0;
        // Player starves too — but the player respawns with no corpse drop.
        if (a.role === 'player') {
          this.killPlayer(a);
        } else {
          this.killAnt(a);
        }
      }
    }
  }

  /**
   * Centralised death: flip alive=false, drop a caste-scaled corpse-food item,
   * fire the death event, zero out velocity. Use this everywhere a non-player
   * ant dies.
   */
  private killAnt(a: AntInternal): void {
    if (!a.alive) return;
    a.alive = false;
    a.deathT = 0;
    a.vx = 0;
    a.vz = 0;
    const corpse = corpseFoodForCaste(a.casteType);
    this.food.spawn(a.x, a.z, corpse.amount, corpse.value, 'corpse');
    this.onEvent?.('death');
  }

  /** Central player-death hook. Kills the yellow ant (no corpse) and releases
   *  all followers so they don't keep chasing a corpse that's about to respawn. */
  private killPlayer(a: AntInternal): void {
    if (!a.alive) return;
    a.alive = false;
    a.deathT = 0;
    a.vx = 0;
    a.vz = 0;
    this.releaseAllFollowers();
    this.onEvent?.('death');
  }

  private releaseAllFollowers(): void {
    for (let i = 0; i < this.ants.length; i++) {
      const f = this.ants[i];
      if (f.role === 'follower') {
        f.role = 'normal';
        f.task = 'search';
        f.carrying = false;
      }
    }
  }

  /**
   * Same-team proximity gifting: well-fed (hunger < 0.3) feeds hungry (hunger > 0.5)
   * when within 0.25m. Each ant limited by a 5s cooldown. Symmetric pair visit.
   */
  private giftTick(): void {
    const ants = this.ants;
    const t = this.elapsed;
    for (let i = 0; i < ants.length; i++) {
      const a = ants[i];
      if (!a.alive || a.nestCycleT > 0) continue;
      if (t - a.lastGiftT < GIFT_COOLDOWN) continue;
      for (let j = i + 1; j < ants.length; j++) {
        const b = ants[j];
        if (!b.alive || b.team !== a.team || b.nestCycleT > 0) continue;
        if (t - b.lastGiftT < GIFT_COOLDOWN) continue;
        const dx = a.x - b.x;
        const dz = a.z - b.z;
        if (dx * dx + dz * dz > GIFT_RADIUS_SQ) continue;
        let donor: AntInternal | null = null;
        let recipient: AntInternal | null = null;
        if (a.hunger < GIFT_WELL_FED && b.hunger > GIFT_HUNGRY) {
          donor = a; recipient = b;
        } else if (b.hunger < GIFT_WELL_FED && a.hunger > GIFT_HUNGRY) {
          donor = b; recipient = a;
        }
        if (!donor || !recipient) continue;
        recipient.hunger = 0;
        donor.hunger = Math.min(GIFT_DONOR_CAP, donor.hunger + GIFT_DONOR_COST);
        donor.lastGiftT = t;
        recipient.lastGiftT = t;
        donor.giftFlashT = GIFT_FLASH_DURATION;
        recipient.giftFlashT = GIFT_FLASH_DURATION;
        donor.giftPartnerId = recipient.id;
        recipient.giftPartnerId = donor.id;
        this.onEvent?.('gift');
        break; // a can only gift once per tick
      }
    }
  }

  /**
   * Player-privileged eat: if the player is alive and within 0.25m of any
   * alive same-team ant whose hunger < 0.3, the player is fed automatically
   * (player hunger -> 0, target hunger -> 0.6). 3s cooldown on the player side.
   */
  /** If the player is within grab-radius of any food pile, bite off one unit
   *  and knock hunger down. Cooldown ~0.4 s between bites so they don't
   *  clear a pile in a single frame. No effect if already full. */
  private playerPileEatTick(): void {
    const p = this.ants[this.playerId];
    if (!p || !p.alive || p.nestCycleT > 0) return;
    if (p.hunger <= 0.02) return;
    if (this.elapsed - this.playerLastFeedT < 0.4) return;
    const item = this.food.tryPickup(p.x, p.z, FORAGE_CONFIG.grabRadius);
    if (!item) return;
    // Each bite reduces hunger by 0.25; sugar piles (value>=4) restore more.
    const hungerDrop = item.value >= 4 ? 0.35 : 0.25;
    p.hunger = Math.max(0, p.hunger - hungerDrop);
    this.playerLastFeedT = this.elapsed;
    p.giftFlashT = GIFT_FLASH_DURATION;  // visual pickup flash
    this.onEvent?.('pickup');
  }

  private playerFeedTick(): void {
    const p = this.ants[this.playerId];
    if (!p || !p.alive || p.nestCycleT > 0) return;
    if (this.elapsed - this.playerLastFeedT < PLAYER_EAT_COOLDOWN) return;
    const ants = this.ants;
    for (let i = 0; i < ants.length; i++) {
      const o = ants[i];
      if (o === p || !o.alive || o.team !== p.team || o.nestCycleT > 0) continue;
      if (o.hunger >= PLAYER_EAT_DONOR_HUNGER_CEIL) continue;
      const dx = o.x - p.x;
      const dz = o.z - p.z;
      if (dx * dx + dz * dz > PLAYER_EAT_RADIUS_SQ) continue;
      p.hunger = 0;
      o.hunger = PLAYER_EAT_DONOR_AFTER;
      this.playerLastFeedT = this.elapsed;
      p.giftFlashT = GIFT_FLASH_DURATION;
      o.giftFlashT = GIFT_FLASH_DURATION;
      this.onEvent?.('gift');
      return;
    }
  }

  /**
   * If the player is dead for >= 2s of deathT, respawn at the red nest center
   * with full HP and zero hunger. Same AntState slot, no new spawn.
   */
  private playerRespawnTick(): void {
    const p = this.ants[this.playerId];
    if (!p || p.alive) return;
    if (p.deathT < PLAYER_RESPAWN_DELAY) return;
    const nest = this.nests[p.team];
    p.alive = true;
    p.hunger = 0;
    p.hp = 1;
    p.deathT = 0;
    p.x = nest.x;
    p.z = nest.z;
    p.y = this.terrain.getHeight(nest.x, nest.z);
    p.vx = 0;
    p.vz = 0;
    p.speed = 0;
    const heading = Math.random() * TAU;
    p.heading = heading;
    p.targetHeading = heading;
    p.task = 'search';
    p.carrying = false;
    p.goal = null;
    p.nestCycleT = 0;
    p.nestCycleDur = 0;
    p.fightWith = -1;
    p.fightT = 0;
    p.attacking = false;
    p.lastGiftT = -Infinity;
    p.giftFlashT = 0;
    this.onEvent?.('respawn');
  }

  /**
   * Continuous melee — any two alive enemies within MELEE_RADIUS damage each
   * other by DMG_PER_ATTACKER_PER_SEC × dt. HP crosses 0 → dead. Each ant's
   * `attacking` flag is set if at least one enemy was in range this tick.
   */
  private meleeDamageTick(dt: number): void {
    const ants = this.ants;
    // Clear attacking flags for alive ants.
    for (let i = 0; i < ants.length; i++) {
      if (ants[i].alive) ants[i].attacking = false;
    }
    const dmg = DMG_PER_ATTACKER_PER_SEC * dt;
    for (let i = 0; i < ants.length; i++) {
      const a = ants[i];
      if (!a.alive || a.nestCycleT > 0) continue;
      for (let j = i + 1; j < ants.length; j++) {
        const b = ants[j];
        if (!b.alive || b.team === a.team || b.nestCycleT > 0) continue;
        const dx = a.x - b.x;
        const dz = a.z - b.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < MELEE_RADIUS_SQ) {
          // Nurses bite with mandibles pre-softened for larva-chewing — the
          // hit lands twice as hard when they're forced into combat.
          // Caste damage profile (hardest → softest): queen > nurse >
          // soldier > worker > scout > breeder. Queens are the most
          // dangerous if they ever reach combat; breeders barely scratch.
          const dmgFromA = dmg * damageMultForCaste(a.casteType);
          const dmgFromB = dmg * damageMultForCaste(b.casteType);
          b.hp -= dmgFromA;
          a.hp -= dmgFromB;
          a.attacking = true;
          b.attacking = true;
          if (a.hp <= 0 && a.alive) {
            if (a.role === 'player') this.killPlayer(a);
            else this.killAnt(a);
          }
          if (b.hp <= 0 && b.alive) {
            if (b.role === 'player') this.killPlayer(b);
            else this.killAnt(b);
          }
        }
      }
    }

    // Also flag attacking for ants that are biting a grasshopper or mobbing a
    // spider. Gives those encounters the same chomp animation + posture.
    const BITE_R2 = 0.15 * 0.15;
    for (let gi = 0; gi < this.grasshopperTargets.length; gi++) {
      const gh = this.grasshopperTargets[gi];
      if (!gh.alive) continue;
      for (let i = 0; i < ants.length; i++) {
        const a = ants[i];
        if (!a.alive || a.nestCycleT > 0) continue;
        const dx = a.x - gh.x;
        const dz = a.z - gh.z;
        if (dx * dx + dz * dz < BITE_R2) a.attacking = true;
      }
    }
    const SPIDER_BITE_R2 = 0.25 * 0.25;
    for (let si = 0; si < this.spiderTargets.length; si++) {
      const sp = this.spiderTargets[si];
      if (!sp.alive) continue;
      for (let i = 0; i < ants.length; i++) {
        const a = ants[i];
        if (!a.alive || a.nestCycleT > 0) continue;
        const dx = a.x - sp.x;
        const dz = a.z - sp.z;
        if (dx * dx + dz * dz < SPIDER_BITE_R2) a.attacking = true;
      }
    }
  }

  /**
   * For an alive forager `a`, pick an enemy to charge at:
   *   1. Nearest visible enemy within ENEMY_SENSE.
   *   2. Otherwise, if a same-team ally within ALLY_FIGHT_SENSE is `attacking`,
   *      find the enemy nearest that ally and chase it — producing the
   *      "ants read each other and swarm in from the nearest side" effect.
   * Returns null if no target found.
   */
  private pickAggroTarget(a: AntInternal): AntInternal | null {
    const ants = this.ants;
    let best: AntInternal | null = null;
    let bestD2 = ENEMY_SENSE_SQ;

    for (let i = 0; i < ants.length; i++) {
      const o = ants[i];
      if (!o.alive || o.team === a.team || o.role === 'player' || o.nestCycleT > 0) continue;
      const dx = o.x - a.x;
      const dz = o.z - a.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; best = o; }
    }
    if (best) return best;

    let nearestAlly: AntInternal | null = null;
    let nearestAllyD2 = ALLY_FIGHT_SENSE_SQ;
    for (let i = 0; i < ants.length; i++) {
      const o = ants[i];
      if (o === a || !o.alive || o.team !== a.team || !o.attacking) continue;
      const dx = o.x - a.x;
      const dz = o.z - a.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < nearestAllyD2) { nearestAllyD2 = d2; nearestAlly = o; }
    }
    if (!nearestAlly) return null;

    let bestEnemy: AntInternal | null = null;
    let bestEnemyD2 = MELEE_RADIUS_SQ * 4;
    for (let i = 0; i < ants.length; i++) {
      const o = ants[i];
      if (!o.alive || o.team === a.team) continue;
      const dx = o.x - nearestAlly.x;
      const dz = o.z - nearestAlly.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestEnemyD2) { bestEnemyD2 = d2; bestEnemy = o; }
    }
    return bestEnemy;
  }

  // ---------- PlayerControl ----------

  setPlayerGoal(x: number, z: number): void {
    const p = this.ants[this.playerId];
    if (!p) return;
    p.goal = { x, z };
    // Any pending pebble action is cancelled by a fresh plain move.
    p.pickupOnArrive = -1;
    p.dropOnArrive = false;
  }

  clearPlayerGoal(): void {
    const p = this.ants[this.playerId];
    if (!p) return;
    p.goal = null;
    p.pickupOnArrive = -1;
    p.dropOnArrive = false;
  }

  summonFollowers(n: number): number {
    const p = this.ants[this.playerId];
    if (!p || n <= 0) return 0;
    let promoted = 0;
    for (let k = 0; k < n; k++) {
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < this.ants.length; i++) {
        const a = this.ants[i];
        // Only promote same-team normal ants that are alive. Enemies don't listen.
        if (a.role !== 'normal' || a.team !== p.team || !a.alive) continue;
        const d = (a.x - p.x) * (a.x - p.x) + (a.z - p.z) * (a.z - p.z);
        if (d < bestDist) { bestDist = d; bestIdx = i; }
      }
      if (bestIdx === -1) break;
      const a = this.ants[bestIdx];
      a.role = 'follower';
      // Wipe every "stuck doing something else" flag so the recruit
      // immediately starts steering at the player. Without these clears,
      // an ant mid-bite, mid-frenzy, mid-scout-report or mid-alarm-march
      // would ignore the summon for a while.
      a.nestCycleT = 0;
      a.nestCycleDur = 0;
      a.pauseTimer = 0;
      a.vx = 0;
      a.vz = 0;
      a.carrying = false;
      a.attacking = false;
      a.pebbleFollower = false;
      a.frenzyPhase = 'pickup';
      a.frenzyTargetX = 0;
      a.frenzyTargetZ = 0;
      a.alarmCommitted = false;
      a.alarmWanderT = 0;
      a.reportKind = 'none';
      a.reportReachedFood = false;
      a.invadingEnemyNest = false;
      // Drop any held pebble at the recruit's feet — followers don't carry
      // pebbles around the map; the parade is its own state machine.
      if (a.heldPebble !== -1) {
        this.pebbleDropFn?.(a.heldPebble, a.x, a.z);
        a.heldPebble = -1;
      }
      const dx = p.x - a.x;
      const dz = p.z - a.z;
      if (dx * dx + dz * dz > 1e-6) {
        a.heading = Math.atan2(dz, dx);
        a.targetHeading = a.heading;
      }
      promoted++;
    }
    return promoted;
  }

  releaseFollowers(fraction: number): number {
    const clamped = Math.max(0, Math.min(1, fraction));
    const followers: AntInternal[] = [];
    for (const a of this.ants) if (a.role === 'follower') followers.push(a);
    const toRelease = Math.floor(followers.length * clamped);
    for (let i = 0; i < toRelease; i++) {
      const f = followers[i];
      f.role = 'normal';
      f.task = 'search';
      f.carrying = false;
      // Released ants head toward the nearest food pile — purposeful dispersal
      // instead of resuming wander. Normal forager AI takes over from there.
      let bestX = 0, bestZ = 0, bestD2 = Infinity;
      for (const item of this.food.items) {
        if (item.amount <= 0) continue;
        const dx = item.x - f.x;
        const dz = item.z - f.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < bestD2) { bestD2 = d2; bestX = item.x; bestZ = item.z; }
      }
      if (bestD2 < Infinity) {
        const h = Math.atan2(bestZ - f.z, bestX - f.x);
        f.heading = h;
        f.targetHeading = h;
      }
    }
    return toRelease;
  }

  /** Seconds remaining on the team-wide "duck for cover" order. While > 0,
   *  every same-team non-player ant heads home and hides. Each U-press
   *  restarts the countdown from 60 s. */
  private recallT = 0;
  private readonly RECALL_DURATION = 60;

  toggleNestRecall(): boolean {
    if (this.recallT > 0) {
      // Already recalling — second press cancels early and releases everyone.
      this.recallT = 0;
      for (const a of this.ants) {
        if (a.alive && a.team === this.playerTeam() && a.role !== 'player') {
          if (a.casteType === 'nurse' || a.casteType === 'queen') continue;
          if (a.nestCycleT > 1000) a.nestCycleT = 0;
        }
      }
      return false;
    }
    this.recallT = this.RECALL_DURATION;
    return true;
  }

  /** Force a recall for an exact duration. Used by external systems
   *  (cold-snap weather event) that need to drive the colony underground
   *  for a known window without colliding with the U-key toggle. */
  setRecall(durationSec: number): void {
    this.recallT = Math.max(this.recallT, durationSec);
  }
  clearRecall(): void {
    if (this.recallT > 0) {
      this.recallT = 0;
      for (const a of this.ants) {
        if (a.alive && a.team === this.playerTeam() && a.role !== 'player') {
          if (a.casteType === 'nurse' || a.casteType === 'queen') continue;
          if (a.nestCycleT > 1000) a.nestCycleT = 0;
        }
      }
    }
  }

  private tickRecall(dt: number): void {
    if (this.recallT <= 0) return;
    this.recallT -= dt;
    if (this.recallT <= 0) {
      this.recallT = 0;
      for (const a of this.ants) {
        if (a.alive && a.team === this.playerTeam() && a.role !== 'player') {
          if (a.casteType === 'nurse' || a.casteType === 'queen') continue; // stay underground
          if (a.nestCycleT > 1000) a.nestCycleT = 0;
        }
      }
    }
  }

  get recallActive(): boolean { return this.recallT > 0; }

  private playerTeam(): 0 | 1 {
    const p = this.ants[this.playerId];
    return p ? p.team : 0;
  }

  get isRecalling(): boolean { return this.recallActive; }

  // ---------- Per-ant step ----------

  private stepAnt(a: AntInternal, dt: number): void {
    if (!a.alive) {
      a.deathT += dt;
      return;
    }

    // Trophalaxis — while a gift flash is active this ant freezes and
    // faces its partner so the shared Trophalaxis animation reads as
    // a mouth-to-mouth feed. Player is exempt; we never lock their
    // controls. Nest-cycle ants also skip since they're underground.
    if (a.giftFlashT > 0 && a.role !== 'player' && a.nestCycleT === 0) {
      const partner = this.ants[a.giftPartnerId];
      if (partner && partner.alive) {
        const h = Math.atan2(partner.z - a.z, partner.x - a.x);
        a.heading = h;
        a.targetHeading = h;
      }
      a.vx = 0;
      a.vz = 0;
      a.speed = 0;
      a.y = this.terrain.getHeight(a.x, a.z);
      return;
    }

    // Nest-recall override — player hit "U". Every same-team ant heads home
    // and hides underground. Queens stay in their chamber; everyone else
    // (soldiers included) dives into the nest.
    if (this.recallActive && a.team === this.playerTeam() && a.role !== 'player'
        && a.casteType !== 'queen') {
      const nest = this.nests[a.team];
      const dx = nest.x - a.x;
      const dz = nest.z - a.z;
      const distSq = dx * dx + dz * dz;
      if (distSq <= nest.radius * nest.radius) {
        a.nestCycleT = 1e9;
        a.nestCycleDur = 1e9;
        a.vx = 0;
        a.vz = 0;
        a.y = this.terrain.getHeight(a.x, a.z);
        return;
      } else {
        // Not home yet — sprint home.
        const heading = Math.atan2(dz, dx);
        a.heading = heading;
        a.targetHeading = heading;
        a.carrying = false;
        a.task = 'return';
        const sprint = this.maxSpeed * 1.8;
        a.vx = Math.cos(heading) * sprint;
        a.vz = Math.sin(heading) * sprint;
        a.x += a.vx * dt;
        a.z += a.vz * dt;
        a.y = this.terrain.getHeight(a.x, a.z);
        return;
      }
    }

    // Queens live in the bottom chamber — never at the surface. Pin her to
    // the nest XZ and force-dive her underground so the surface visual sinks
    // her below the terrain. The underground 3D scene renders the authored
    // queen GLB separately in the queen chamber.
    if (a.casteType === 'queen') {
      const nest = this.nests[a.team];
      a.x = nest.x;
      a.z = nest.z;
      a.vx = 0;
      a.vz = 0;
      a.speed = 0;
      a.nestCycleT = 1e9;
      a.nestCycleDur = 1e9;
      a.y = this.terrain.getHeight(a.x, a.z);
      return;
    }

    // Nurses dive on spawn and effectively stay underground forever — they
    // tend the queen and larvae below the surface. First step after spawn we
    // detect the fresh state (nestCycleT === 0) and kick off a permanent dive.
    if (a.casteType === 'nurse' && a.nestCycleT === 0) {
      a.nestCycleT = 1e9;
      a.nestCycleDur = 1e9;
      return;
    }

    // Soldiers station-keep on a ring around the nest entrance. Each soldier
    // is assigned a deterministic slot angle (golden-angle spaced by id) so
    // a rim of sentries fills the circle evenly; they march to their slot
    // then stand still facing outward. Alarm override and follower-summon
    // both bypass this block. A scout-reported sighting ALSO pulls soldiers
    // off their rim and sends them charging the reported (x, z) — even when
    // that spot is outside the nest's own defence radius, which is the whole
    // point of scouting.
    if (a.casteType === 'soldier' && a.role !== 'follower') {
      const alarm = this.nestAlarms[a.team];
      const scoutAlarm = this.scoutAlarms[a.team];

      // Direct engagement: any opposing-team ant within soldier sight range
      // is an immediate combat target. Closer than the stock forager aggro
      // (3 m) — soldiers are the ones who drop everything and rush. Overrides
      // role dispatch but not a home-nest alarm (which has its own logic).
      if (!alarm?.active) {
        const SOLDIER_ENGAGE_R = 5.0;
        const engageR2 = SOLDIER_ENGAGE_R * SOLDIER_ENGAGE_R;
        let bestD2 = engageR2;
        let tx = 0, tz = 0, found = false;
        for (let i = 0; i < this.ants.length; i++) {
          const o = this.ants[i];
          if (!o.alive || o.team === a.team || o.nestCycleT > 0 || o.role === 'player') continue;
          const dx = o.x - a.x;
          const dz = o.z - a.z;
          const d2 = dx * dx + dz * dz;
          if (d2 < bestD2) { bestD2 = d2; tx = o.x; tz = o.z; found = true; }
        }
        if (found) {
          const heading = Math.atan2(tz - a.z, tx - a.x);
          a.heading = heading;
          a.targetHeading = heading;
          const sprint = this.maxSpeed * 1.6;
          a.vx = Math.cos(heading) * sprint;
          a.vz = Math.sin(heading) * sprint;
          a.x += a.vx * dt;
          a.z += a.vz * dt;
          a.y = this.terrain.getHeight(a.x, a.z);
          a.speed = Math.hypot(a.vx, a.vz);
          return;
        }
      }

      // Latch a fresh scout-alarm as soon as we see one. `alarmCommitted`
      // stays set until the soldier has actually reached the reported spot
      // AND patrolled it — the scout-alarm TTL expiring mid-march no longer
      // yanks the column back to its rim.
      if (scoutAlarm?.active && !a.alarmCommitted && !alarm?.active) {
        a.alarmCommitted = true;
        a.alarmX = scoutAlarm.x;
        a.alarmZ = scoutAlarm.z;
        a.alarmWanderT = 0;
      }

      // Home-nest alarm always wins over a scout-alarm commitment — bring
      // the soldier back to defend the queen.
      if (alarm?.active && a.alarmCommitted) {
        a.alarmCommitted = false;
      }

      if (a.alarmCommitted) {
        const dxA = a.alarmX - a.x;
        const dzA = a.alarmZ - a.z;
        const dA2 = dxA * dxA + dzA * dzA;
        if (dA2 > SOLDIER_ALARM_ARRIVAL_R_SQ) {
          // Still en route — sprint to the spot and ignore everything else.
          const heading = Math.atan2(dzA, dxA);
          a.heading = heading;
          a.targetHeading = heading;
          const sprint = this.maxSpeed * 1.8;
          a.vx = Math.cos(heading) * sprint;
          a.vz = Math.sin(heading) * sprint;
          a.x += a.vx * dt;
          a.z += a.vz * dt;
          a.y = this.terrain.getHeight(a.x, a.z);
          a.speed = Math.hypot(a.vx, a.vz);
          return;
        }
        // Arrived. Patrol the area for a fixed duration so lingering
        // threats get mopped up and soldiers don't immediately pivot home
        // the instant the scoutAlarm expires. Once the timer runs out we
        // unlatch and the ant is free to walk back to its rim slot.
        if (a.alarmWanderT <= 0) a.alarmWanderT = SOLDIER_ALARM_PATROL_DURATION;
        a.alarmWanderT -= dt;
        if (a.alarmWanderT > 0) {
          // If the alarm target is the enemy nest itself (scout-reported),
          // the soldier flips into invasion mode — chew on the enemy queen
          // or nurses the same way player followers do. Otherwise just
          // patrol the spot.
          const enemyTeam = (1 - a.team) as 0 | 1;
          const eNest = this.nests[enemyTeam];
          const alarmNearNest =
            (a.alarmX - eNest.x) ** 2 + (a.alarmZ - eNest.z) ** 2
              < INVASION_TRIGGER_RADIUS * INVASION_TRIGGER_RADIUS;
          if (alarmNearNest && this.teamQueenAlive(enemyTeam)) {
            this.stepFollowerInvasion(a, eNest, dt, enemyTeam);
            return;
          }
          const ang = a.id * 2.39996 + this.elapsed * 0.7;
          const tx = a.alarmX + Math.cos(ang) * SOLDIER_ALARM_PATROL_RADIUS;
          const tz = a.alarmZ + Math.sin(ang) * SOLDIER_ALARM_PATROL_RADIUS;
          const heading = Math.atan2(tz - a.z, tx - a.x);
          a.heading = heading;
          a.targetHeading = heading;
          const walk = this.maxSpeed * 0.9;
          a.vx = Math.cos(heading) * walk;
          a.vz = Math.sin(heading) * walk;
          a.x += a.vx * dt;
          a.z += a.vz * dt;
          a.y = this.terrain.getHeight(a.x, a.z);
          a.speed = Math.hypot(a.vx, a.vz);
          return;
        }
        a.alarmCommitted = false;
      }

      if (!alarm?.active) {
        // Tick the role-reroll clock. When it lapses, pick a fresh role
        // with weighted randomness — 45 % guard, 35 % patrol, 20 % escort —
        // so at any moment the colony has a rough mix of jobs but the
        // individuals rotate through them over time.
        a.soldierRoleT -= dt;
        if (a.soldierRoleT <= 0) {
          const r = Math.random();
          a.soldierRole = r < 0.45 ? 'guard' : r < 0.80 ? 'patrol' : 'escort';
          a.soldierRoleT = 180 + Math.random() * 300;  // 3–8 min
          a.guardTargetX = 0;
          a.guardTargetZ = 0;
        }
        if (a.soldierRole === 'guard') { this.stepSoldierGuard(a, dt); return; }
        if (a.soldierRole === 'patrol') { this.stepSoldierPatrol(a, dt); return; }
        if (a.soldierRole === 'escort') { this.stepSoldierEscort(a, dt); return; }
      }
    }

    // Pebble parade — same-team workers who saw the player pick up a
    // pebble rally to grab one and follow. Flag is cleared by
    // endPebbleRally when the player drops (or by player death).
    if (a.pebbleFollower && a.role === 'normal' && a.casteType === 'worker') {
      this.stepPebbleFollower(a, dt);
      return;
    }

    // Workers — the bulk foragers — don't fan out randomly. They hang near the
    // nest until they detect a food trail, then ride it out to the source.
    // Carrying workers are already beelining home, so only apply the leash when
    // empty-handed.
    if (a.casteType === 'worker' && !a.carrying) {
      const trail = this.pheromones[a.team].sample(a.x, a.z, 0);
      const onFoodTrail = trail > TRAIL_THRESHOLD;
      const nest = this.nests[a.team];
      const dx = nest.x - a.x;
      const dz = nest.z - a.z;
      const leash = 5.0;
      if (!onFoodTrail && dx * dx + dz * dz > leash * leash) {
        const desired = Math.atan2(dz, dx);
        const delta = wrapAngle(desired - a.targetHeading);
        a.targetHeading = wrapAngle(a.targetHeading + delta * 4.0 * dt);
      }
    }

    // Breeders stay very close to the nest (2.5m leash).
    if (a.casteType === 'breeder') {
      const nest = this.nests[a.team];
      const dx = nest.x - a.x;
      const dz = nest.z - a.z;
      const leash = 2.5;
      if (dx * dx + dz * dz > leash * leash) {
        const desired = Math.atan2(dz, dx);
        const delta = wrapAngle(desired - a.targetHeading);
        a.targetHeading = wrapAngle(a.targetHeading + delta * 5.0 * dt);
      }
    }

    // Scouts are the colony's long-range eyes. When not already reporting,
    // they scan a wide radius for enemies and food; on a sighting they flip
    // into reporting mode and sprint home laying a heavy pheromone stripe so
    // the rest of the colony can find the spot. Only runs above ground.
    if (a.casteType === 'scout' && a.role === 'normal' && !a.carrying && a.nestCycleT === 0) {
      if (a.reportCooldown > 0) a.reportCooldown = Math.max(0, a.reportCooldown - dt);
      if (a.reportKind === 'none' && a.reportCooldown <= 0 && a.hunger < HUNGER_STARVING) {
        this.scoutScan(a);
      }
      if (a.reportKind !== 'none') {
        this.tickScoutReport(a, dt);
        return;
      }
      // Default outward-bias so idle scouts keep pushing exploration.
      const nest = this.nests[a.team];
      const dx = a.x - nest.x;
      const dz = a.z - nest.z;
      if (dx * dx + dz * dz > 1e-6) {
        const desired = Math.atan2(dz, dx);
        const delta = wrapAngle(desired - a.targetHeading);
        a.targetHeading = wrapAngle(a.targetHeading + delta * 1.5 * dt);
      }
    }

    // Actively biting an enemy — slow right down, let damage tick do its
    // work. Followers + the player are exempt: a summon ("2") needs to
    // actually pull them off a fight, and the player must always be
    // controllable. nearestEnemyInMelee also requires a LIVE target, so
    // foragers stuck mid-bite on an ant that just died fall through to
    // the role dispatch and resume foraging this same tick.
    if (a.attacking && a.role !== 'follower' && a.role !== 'player') {
      const nearest = this.nearestEnemyInMelee(a);
      if (nearest) {
        const desired = Math.atan2(nearest.z - a.z, nearest.x - a.x);
        const dh = wrapAngle(desired - a.heading);
        const maxStep = this.headingSlew * dt * 3;
        a.heading += Math.abs(dh) <= maxStep ? dh : Math.sign(dh) * maxStep;
        a.heading = wrapAngle(a.heading);
        a.targetHeading = a.heading;
        // Heavy drag so biting ants mostly stay on target but don't freeze.
        const k = Math.max(0, 1 - 8 * dt);
        a.vx *= k;
        a.vz *= k;
        a.speed = Math.hypot(a.vx, a.vz);
        return;
      }
      // No live enemy in melee — the target died this frame. Clear the
      // flag and fall through so the ant resumes its normal behaviour
      // immediately instead of standing on the corpse for another tick.
      a.attacking = false;
    }

    // Underground in the nest — skip sim, just tick the dive/hide/emerge clock.
    if (a.nestCycleT > 0) {
      a.nestCycleT = Math.max(0, a.nestCycleT - dt);
      const n = this.nests[a.team];
      a.x = n.x;
      a.z = n.z;
      a.vx = 0;
      a.vz = 0;
      a.speed = 0;
      a.y = this.terrain.getHeight(a.x, a.z);
      if (a.nestCycleT <= 0) {
        // Emerge facing a random outward direction.
        const ang = Math.random() * TAU;
        a.heading = ang;
        a.targetHeading = ang;
      }
      return;
    }

    if (a.role === 'player') {
      this.stepPlayer(a, dt);
    } else if (a.role === 'follower') {
      this.stepFollower(a, dt);
    } else {
      this.stepForager(a, dt);
    }
    const oldX = a.x;
    const oldZ = a.z;
    this.integrate(a, dt);
    this.resolveCollisions(a, oldX, oldZ);
    this.applyTaskTransitions(a);
    this.applyBounds(a);
    a.y = this.terrain.getHeight(a.x, a.z);
    a.speed = Math.hypot(a.vx, a.vz);
  }

  /**
   * After integration, enforce hard constraints:
   *   - cannot enter a rock (circular obstacle)
   *   - cannot cross onto too-steep terrain
   * On violation, revert to last good position and deflect velocity.
   */
  private resolveCollisions(a: AntInternal, oldX: number, oldZ: number): void {
    // The player walks through rocks — less frustrating than being bounced
    // off dozens of times along a click-to-move path.
    if (a.role === 'player') return;
    // Steep-slope barrier removed — ants now walk up/down whatever the
    // terrain offers; each tick they snap Y to terrain.getHeight which
    // keeps their feet on the surface.
    void oldX; void oldZ;

    // Rock + grass + pebble avoidance. Grass/pebbles come via spatial-grid
    // callback so we don't brute-force thousands of entries per ant per
    // frame. When the ant penetrates an obstacle we push it to the edge
    // AND cancel just the inward component of velocity (slide tangent)
    // rather than reflecting — gives a "walk around" look instead of a
    // bounce. Frenzied workers that are actively targeting a pebble to
    // pick up get a pass so they can actually reach their target.
    const nearby = this.nearbyObstaclesFn ? this.nearbyObstaclesFn(a.x, a.z) : [];
    const obsSources: ReadonlyArray<readonly { x: number; z: number; radius: number }[]> = [this.obstacles, nearby];
    for (const src of obsSources) for (let i = 0; i < src.length; i++) {
      const o = src[i];
      const dx = a.x - o.x;
      const dz = a.z - o.z;
      const r = o.radius + ANT_BODY_RADIUS;
      const d2 = dx * dx + dz * dz;
      if (d2 < r * r) {
        const d = Math.sqrt(d2) || 1e-6;
        const nX = dx / d;
        const nZ = dz / d;
        a.x = o.x + nX * r;
        a.z = o.z + nZ * r;
        const vdotn = a.vx * nX + a.vz * nZ;
        if (vdotn < 0) {
          // Remove the inward component; keep the tangent so the ant
          // slides around the obstacle edge smoothly.
          a.vx -= vdotn * nX;
          a.vz -= vdotn * nZ;
        }
        // Rotate the heading target to a tangent — biases next-frame
        // steering into "around" rather than straight back into the rock.
        const sp = Math.hypot(a.vx, a.vz);
        if (sp > 1e-4) {
          a.heading = Math.atan2(a.vz, a.vx);
          a.targetHeading = a.heading;
        }
      }
    }
  }

  private stepPlayer(a: AntInternal, dt: number): void {
    // Player ignores pheromones, pauses, and foraging. Pure navigation.
    a.pauseTimer = 0;
    if (a.goal) {
      const dx = a.goal.x - a.x;
      const dz = a.goal.z - a.z;
      const dist = Math.hypot(dx, dz);
      // Pebble-pickup goals use a larger arrival radius than plain move
      // goals — the player just needs to be mouth-close to the pebble,
      // not standing on top of the target point.
      const arrival = a.pickupOnArrive !== -1 ? 0.35 : PLAYER_ARRIVAL_RADIUS;
      if (dist < arrival) {
        // Run the pending action on arrival, then clear goal.
        if (a.pickupOnArrive !== -1 && a.heldPebble === -1) {
          const idx = this.pebblePickupFn?.(a.x, a.z, 0.8, a.id) ?? -1;
          if (idx !== -1) {
            a.heldPebble = idx;
            this.startPebbleRally(a.x, a.z);
            this.onEvent?.('pickup');
          }
        }
        if (a.dropOnArrive && a.heldPebble !== -1) {
          this.pebbleDropFn?.(a.heldPebble, a.x, a.z);
          a.heldPebble = -1;
          this.endPebbleRally(a.x, a.z);
        }
        a.pickupOnArrive = -1;
        a.dropOnArrive = false;
        a.goal = null;
        // Hard stop — zero velocity so the body doesn't coast past.
        a.vx = 0;
        a.vz = 0;
      } else {
        // Snap direction and drive the player at full sprint speed every tick.
        // No approach braking: full go until we're inside the arrival radius,
        // then hard stop in the `dist < PLAYER_ARRIVAL_RADIUS` branch above.
        const desired = Math.atan2(dz, dx);
        a.targetHeading = desired;
        a.heading = desired;
        const sprint = this.maxSpeed * PLAYER_SPEED_MULTIPLIER;
        a.vx = Math.cos(desired) * sprint;
        a.vz = Math.sin(desired) * sprint;
      }
    } else {
      // No goal — coast to a stop rather than drifting forever.
      const decel = 1 - Math.min(1, 6 * dt);
      a.vx *= decel;
      a.vz *= decel;
    }
  }

  /** Pebble-frenzy worker step. Alternates between "go to nearest pebble"
   *  and "carry it a short distance then drop", propagating the frenzy to
  /** Pebble parade worker step. Three phases:
   *    pickup — walk to a free pebble, grab it, switch to follow
   *    follow — trail the player while carrying; snap to follow-radius
   *    drop   — player dropped, walk to drop jitter spot, drop, demote
   *  Runs manual integration + applyBounds because stepAnt returns right
   *  after this (same pattern as the soldier-sprint branches).
   */
  private stepPebbleFollower(a: AntInternal, dt: number): void {
    const p = this.ants[this.playerId];
    if (!p || !p.alive) {
      // Player's gone — drop anywhere and clear the flag.
      if (a.heldPebble !== -1) {
        this.pebbleDropFn?.(a.heldPebble, a.x, a.z);
        a.heldPebble = -1;
      }
      a.pebbleFollower = false;
      return;
    }
    a.pauseTimer = 0;
    a.carrying = false;

    // Auto-flip pickup → follow when we're already holding a pebble (e.g.
    // just grabbed it last frame). Drop phase is only entered via
    // endPebbleRally so never auto-switches away from it.
    if (a.frenzyPhase === 'pickup' && a.heldPebble !== -1) {
      a.frenzyPhase = 'follow';
    }

    // Resolve the current target based on phase.
    if (a.frenzyPhase === 'pickup' && (a.frenzyTargetX === 0 && a.frenzyTargetZ === 0)) {
      const idx = this.pebbleClosestFn?.(a.x, a.z) ?? -1;
      if (idx === -1) {
        // Nothing to grab — just follow the player empty-handed.
        a.frenzyPhase = 'follow';
      } else {
        const pos = this.pebblePosFn?.(idx);
        if (pos) {
          a.frenzyTargetX = pos.x;
          a.frenzyTargetZ = pos.z;
        }
      }
    }

    let targetX: number;
    let targetZ: number;
    let arrivalR: number;
    let sp: number;

    if (a.frenzyPhase === 'pickup') {
      targetX = a.frenzyTargetX;
      targetZ = a.frenzyTargetZ;
      arrivalR = FRENZY_PICK_RADIUS;
      sp = this.maxSpeed * 1.2;
    } else if (a.frenzyPhase === 'follow') {
      // Trail a short distance behind the player — offset by id so the
      // parade doesn't collapse onto one point.
      const offsetAng = (a.id * 0.7919) % TAU;
      const offsetR = 0.4 + ((a.id * 13) % 5) * 0.12;
      targetX = p.x + Math.cos(offsetAng) * offsetR;
      targetZ = p.z + Math.sin(offsetAng) * offsetR;
      arrivalR = 0.5;
      sp = this.maxSpeed * 1.0;
    } else {
      // drop phase — target was set by endPebbleRally.
      targetX = a.frenzyTargetX;
      targetZ = a.frenzyTargetZ;
      arrivalR = 0.4;
      sp = this.maxSpeed * 1.0;
    }

    const dx = targetX - a.x;
    const dz = targetZ - a.z;
    const dist = Math.hypot(dx, dz);

    if (dist < arrivalR) {
      if (a.frenzyPhase === 'pickup') {
        const idx = this.pebblePickupFn?.(a.x, a.z, FRENZY_PICK_RADIUS * 1.8, a.id) ?? -1;
        if (idx !== -1) {
          a.heldPebble = idx;
          a.frenzyPhase = 'follow';
        } else {
          // Target got stolen — rescan next frame.
          a.frenzyTargetX = 0;
          a.frenzyTargetZ = 0;
        }
      } else if (a.frenzyPhase === 'drop') {
        if (a.heldPebble !== -1) {
          this.pebbleDropFn?.(a.heldPebble, a.x, a.z);
          a.heldPebble = -1;
        }
        a.pebbleFollower = false;
        a.frenzyTargetX = 0;
        a.frenzyTargetZ = 0;
      }
      // follow phase: we're close enough to the player — loiter, no-op.
      a.vx *= 0.6;
      a.vz *= 0.6;
    } else {
      const h = Math.atan2(dz, dx);
      a.heading = h;
      a.targetHeading = h;
      a.vx = Math.cos(h) * sp;
      a.vz = Math.sin(h) * sp;
    }

    // Manual integration — bypasses obstacle collision so followers can
    // get to the pile even when pebbles stack there. Same integration
    // pattern as the soldier-sprint branches.
    a.x += a.vx * dt;
    a.z += a.vz * dt;
    a.y = this.terrain.getHeight(a.x, a.z);
    a.speed = Math.hypot(a.vx, a.vz);
    this.applyBounds(a);
  }

  private stepFollower(a: AntInternal, dt: number): void {
    const p = this.ants[this.playerId];
    if (!p) {
      this.stepForager(a, dt);
      return;
    }
    a.pauseTimer = 0;

    // Enemy-nest invasion: once a follower is brought within INVASION_TRIGGER
    // of the opposing nest, he peels off the player's train and commits to
    // attacking the queen. `invadingEnemyNest` stays latched until the enemy
    // queen is dead (or the attacker dies). Re-triggered every frame so late
    // arrivals also commit when they wander into range.
    const enemyTeam = (1 - a.team) as 0 | 1;
    const enemyNest = this.nests[enemyTeam];
    const edx = a.x - enemyNest.x;
    const edz = a.z - enemyNest.z;
    const ed2 = edx * edx + edz * edz;
    if (!a.invadingEnemyNest && ed2 < INVASION_TRIGGER_RADIUS * INVASION_TRIGGER_RADIUS) {
      a.invadingEnemyNest = true;
    }
    if (a.invadingEnemyNest) {
      const enemyQueenAlive = this.teamQueenAlive(enemyTeam);
      if (!enemyQueenAlive) {
        // Mission accomplished — fall back to normal follow behaviour.
        a.invadingEnemyNest = false;
      } else {
        this.stepFollowerInvasion(a, enemyNest, dt, enemyTeam);
        return;
      }
    }

    // Combat break-out — only engages once the follower has actually
    // caught up to the player. Otherwise followers got hijacked by any
    // grasshopper/spider that drifted within range during the long
    // walk over, instead of reaching the player who summoned them.
    const distToPlayer2Pre = (p.x - a.x) * (p.x - a.x) + (p.z - a.z) * (p.z - a.z);
    if (distToPlayer2Pre < 4 * 4) {
      const engageHead = this.engageNearestEnemy(a, 4.5);
      if (engageHead !== null) {
        a.targetHeading = engageHead;
        return;
      }
    }

    // If the player is standing still AND this follower has already reached
    // the vicinity, break line formation and patrol around the player.
    const playerSpeed = Math.hypot(p.vx, p.vz);
    const distToPlayer2 = (p.x - a.x) * (p.x - a.x) + (p.z - a.z) * (p.z - a.z);
    if (playerSpeed < 0.04 && distToPlayer2 < 9) {
      this.stepFollowerPatrol(a, p);
      return;
    }

    // Beeline straight at the player. No conga-line, no leader-chain —
    // every follower steers directly toward the player position. Light
    // separation keeps them from stacking on top of each other; the
    // headingSlew built into integrate() smooths the turn.
    let desired = Math.atan2(p.z - a.z, p.x - a.x);

    let sepX = 0;
    let sepZ = 0;
    const sepR2 = FOLLOWER_SEPARATION_RADIUS * FOLLOWER_SEPARATION_RADIUS;
    for (let i = 0; i < this.ants.length; i++) {
      const o = this.ants[i];
      if (o === a || o.role !== 'follower') continue;
      const odx = a.x - o.x;
      const odz = a.z - o.z;
      const d2 = odx * odx + odz * odz;
      if (d2 > 0 && d2 < sepR2) {
        const inv = 1 / d2;
        sepX += odx * inv;
        sepZ += odz * inv;
      }
    }
    if (sepX !== 0 || sepZ !== 0) {
      const sepHeading = Math.atan2(sepZ, sepX);
      const blend = wrapAngle(sepHeading - desired);
      desired = wrapAngle(desired + blend * 0.12);
    }
    a.targetHeading = desired;

    // Ease off if we're already on top of the player so we don't ram them.
    if (distToPlayer2 < FOLLOWER_CLOSE_RADIUS * FOLLOWER_CLOSE_RADIUS) {
      const k = Math.max(0, 1 - 8 * dt);
      a.vx *= k;
      a.vz *= k;
    }
  }

  /** Idle-patrol behaviour: march in a slow ring around the stopped player,
   *  hunting for trouble. If any enemy (other-team ant or spider) is inside
   *  `huntR`, charge them; otherwise circle at `orbitR` spaced by golden
   *  angle so the escort spreads evenly instead of clustering. */
  /** Is the queen of `team` still on the roster? Surface queens stay pinned
   *  at the nest with nestCycleT≈∞ but remain valid AntInternal entries we
   *  can damage. Returns false once she's been killed (or never spawned). */
  /** Guard duty: loose random-walk within a 1.8 m annulus around the nest
   *  entrance. Picks micro-targets and walks to them instead of following
   *  any fixed pose, so the group reads as ants milling around the hole. */
  private stepSoldierGuard(a: AntInternal, dt: number): void {
    const nest = this.nests[a.team];
    const INNER = 0.6;
    const OUTER = 1.8;

    // Standing-watch pause — when pauseTimer is hot the guard stands at
    // its post facing outward (away from the nest) and doesn't move.
    // Without this they re-pick a target every frame and zig-zag, which
    // reads as spinning.
    if (a.pauseTimer > 0) {
      a.pauseTimer = Math.max(0, a.pauseTimer - dt);
      const outward = Math.atan2(a.z - nest.z, a.x - nest.x);
      a.heading = outward;
      a.targetHeading = outward;
      a.vx = 0;
      a.vz = 0;
      a.speed = 0;
      a.y = this.terrain.getHeight(a.x, a.z);
      return;
    }

    const dx = a.guardTargetX - a.x;
    const dz = a.guardTargetZ - a.z;
    const d2 = dx * dx + dz * dz;

    // Arrived at post (or no target yet) — pick a new spot, then stand
    // watch for a few seconds before walking again.
    if (d2 < 0.04 || (a.guardTargetX === 0 && a.guardTargetZ === 0)) {
      // Bias the new target away from the current location so the guard
      // takes meaningful steps rather than micro-shuffling.
      let ang: number;
      let tries = 0;
      do {
        ang = Math.random() * Math.PI * 2;
        const r = INNER + Math.random() * (OUTER - INNER);
        a.guardTargetX = nest.x + Math.cos(ang) * r;
        a.guardTargetZ = nest.z + Math.sin(ang) * r;
        tries++;
      } while (tries < 4 && (a.guardTargetX - a.x) ** 2 + (a.guardTargetZ - a.z) ** 2 < 0.5);
      a.pauseTimer = 2 + Math.random() * 3;  // 2–5 s on watch before next step
      return;
    }

    const tdx = a.guardTargetX - a.x;
    const tdz = a.guardTargetZ - a.z;
    const heading = Math.atan2(tdz, tdx);
    a.heading = heading;
    a.targetHeading = heading;
    const walk = this.maxSpeed * 0.5;
    a.vx = Math.cos(heading) * walk;
    a.vz = Math.sin(heading) * walk;
    a.x += a.vx * dt;
    a.z += a.vz * dt;
    a.y = this.terrain.getHeight(a.x, a.z);
    a.speed = Math.hypot(a.vx, a.vz);
  }

  /** Perimeter patrol: walk a wide ring around the nest at ~4-6 m with an
   *  id-phase offset so a loose rotating column forms. */
  private stepSoldierPatrol(a: AntInternal, dt: number): void {
    const nest = this.nests[a.team];
    const patrolR = 4.5 + Math.sin(a.id * 1.7) * 0.8;    // individual radius jitter
    const baseAng = (a.id * 2.39996) % (Math.PI * 2);
    const curAng = baseAng + this.elapsed * 0.055;       // ~115 s lap
    const tx = nest.x + Math.cos(curAng) * patrolR;
    const tz = nest.z + Math.sin(curAng) * patrolR;
    const heading = Math.atan2(tz - a.z, tx - a.x);
    a.heading = heading;
    a.targetHeading = heading;
    const walk = this.maxSpeed * 0.9;
    a.vx = Math.cos(heading) * walk;
    a.vz = Math.sin(heading) * walk;
    a.x += a.vx * dt;
    a.z += a.vz * dt;
    a.y = this.terrain.getHeight(a.x, a.z);
    a.speed = Math.hypot(a.vx, a.vz);
  }

  /** Escort duty: follow the colony's own food-trail pheromone outbound
   *  (shadowing foragers). If no trail is nearby, wander near the nest. If
   *  we've drifted more than 12 m from home, head back in. Enemies in melee
   *  range are still handled by the global meleeDamageTick. */
  private stepSoldierEscort(a: AntInternal, dt: number): void {
    const nest = this.nests[a.team];
    const dx = nest.x - a.x;
    const dz = nest.z - a.z;
    const homeD = Math.hypot(dx, dz);
    if (homeD > 12) {
      // Too far — march home.
      const heading = Math.atan2(dz, dx);
      a.heading = heading;
      a.targetHeading = heading;
      const walk = this.maxSpeed * 0.9;
      a.vx = Math.cos(heading) * walk;
      a.vz = Math.sin(heading) * walk;
      a.x += a.vx * dt;
      a.z += a.vz * dt;
      a.y = this.terrain.getHeight(a.x, a.z);
      a.speed = Math.hypot(a.vx, a.vz);
      return;
    }
    // Sample food-trail ahead in three directions; if any reads strong, go
    // toward it (outbound along the forager column).
    const { senseDistance, senseAngle } = FORAGE_CONFIG;
    const ph = this.pheromones[a.team];
    const hC = a.heading, hL = hC + senseAngle, hR = hC - senseAngle;
    const sC = ph.sample(a.x + Math.cos(hC) * senseDistance, a.z + Math.sin(hC) * senseDistance, 0);
    const sL = ph.sample(a.x + Math.cos(hL) * senseDistance, a.z + Math.sin(hL) * senseDistance, 0);
    const sR = ph.sample(a.x + Math.cos(hR) * senseDistance, a.z + Math.sin(hR) * senseDistance, 0);
    const best = Math.max(sC, sL, sR);
    let heading: number;
    if (best > TRAIL_THRESHOLD) {
      heading = best === sC ? hC : best === sL ? hL : hR;
    } else {
      // No trail — drift outward from the nest with a slow rotation so
      // escorts fan out rather than clumping.
      const outward = homeD > 1 ? Math.atan2(a.z - nest.z, a.x - nest.x) : Math.random() * Math.PI * 2;
      heading = outward + Math.sin(a.id * 0.8 + this.elapsed * 0.4) * 0.9;
    }
    a.heading = heading;
    a.targetHeading = heading;
    const walk = this.maxSpeed * 0.85;
    a.vx = Math.cos(heading) * walk;
    a.vz = Math.sin(heading) * walk;
    a.x += a.vx * dt;
    a.z += a.vz * dt;
    a.y = this.terrain.getHeight(a.x, a.z);
    a.speed = Math.hypot(a.vx, a.vz);
  }

  private teamQueenAlive(team: 0 | 1): boolean {
    for (let i = 0; i < this.ants.length; i++) {
      const o = this.ants[i];
      if (o.team === team && o.casteType === 'queen' && o.alive) return true;
    }
    return false;
  }

  /** Follower committed to invading the opposing nest. Converges on the
   *  nest centre, orbits at close range, and chews on the enemy queen and
   *  any reachable nurses. Their presence on the surface is picked up by
   *  `computeNestAlarms` each tick so enemy defenders come swarming. */
  private stepFollowerInvasion(a: AntInternal, enemyNest: NestConfig, dt: number, enemyTeam: 0 | 1): void {
    const edx = enemyNest.x - a.x;
    const edz = enemyNest.z - a.z;
    const ed2 = edx * edx + edz * edz;
    if (ed2 > INVASION_ORBIT_RADIUS * INVASION_ORBIT_RADIUS) {
      // Still closing — sprint straight to the hole.
      a.targetHeading = Math.atan2(edz, edx);
      return;
    }
    // In attack range. Orbit the hole so multiple invaders don't pile into
    // one point, and deal damage to queen + any alive nurse each tick. Queen
    // is priority; if she's down a fallback pass picks off a nurse so the
    // raid keeps doing work while the colony promotes a successor.
    const slot = (a.id * 2.39996) + this.elapsed * 1.5;
    const tx = enemyNest.x + Math.cos(slot) * INVASION_ORBIT_RADIUS;
    const tz = enemyNest.z + Math.sin(slot) * INVASION_ORBIT_RADIUS;
    a.targetHeading = Math.atan2(tz - a.z, tx - a.x);
    a.attacking = true;
    let queen: AntInternal | null = null;
    let nurse: AntInternal | null = null;
    for (let i = 0; i < this.ants.length; i++) {
      const o = this.ants[i];
      if (!o.alive || o.team !== enemyTeam) continue;
      if (o.casteType === 'queen') { queen = o; break; }
      if (o.casteType === 'nurse' && !nurse) nurse = o;
    }
    const target = queen ?? nurse;
    if (target) {
      const dps = target.casteType === 'queen' ? INVADER_QUEEN_DPS : INVADER_NURSE_DPS;
      target.hp -= dps * dt;
      if (target.hp <= 0 && target.alive) this.killAnt(target);
    }
  }

  private stepFollowerPatrol(a: AntInternal, p: AntInternal): void {
    const orbitR = 1.6;

    // Hunt: a nearby enemy outranks the patrol orbit. Use the shared
    // engagement helper so patrolling followers also break clean around
    // a target instead of stacking single-file into it.
    const engageHead = this.engageNearestEnemy(a, 5.0);
    let desired: number;
    if (engageHead !== null) {
      desired = engageHead;
    } else {
      // Golden-angle slot gives each follower a stable orbit phase; elapsed
      // drifts the whole ring so they actively march instead of standing at
      // fixed posts.
      const slot = (a.id * 2.39996) + this.elapsed * 0.6;
      const tx = p.x + Math.cos(slot) * orbitR;
      const tz = p.z + Math.sin(slot) * orbitR;
      desired = Math.atan2(tz - a.z, tx - a.x);
    }
    a.targetHeading = desired;
  }

  /** Find the closest enemy ant / spider / grasshopper within `radius`
   *  and return a heading toward it with a strong separation push from
   *  teammate followers — the swarm fans into a circle around the
   *  target rather than queueing single-file. Returns null if no enemy
   *  is in range. */
  private engageNearestEnemy(a: AntInternal, radius: number): number | null {
    const r2 = radius * radius;
    let bestD2 = r2;
    let tx = 0, tz = 0, found = false;
    for (let i = 0; i < this.ants.length; i++) {
      const o = this.ants[i];
      if (!o.alive || o.team === a.team || o.nestCycleT > 0 || o.role === 'player') continue;
      const dx = o.x - a.x;
      const dz = o.z - a.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; tx = o.x; tz = o.z; found = true; }
    }
    for (let i = 0; i < this.spiderTargets.length; i++) {
      const sp = this.spiderTargets[i];
      if (!sp.alive) continue;
      const dx = sp.x - a.x;
      const dz = sp.z - a.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; tx = sp.x; tz = sp.z; found = true; }
    }
    for (let i = 0; i < this.grasshopperTargets.length; i++) {
      const gh = this.grasshopperTargets[i];
      if (!gh.alive) continue;
      const dx = gh.x - a.x;
      const dz = gh.z - a.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; tx = gh.x; tz = gh.z; found = true; }
    }
    if (!found) return null;

    let desired = Math.atan2(tz - a.z, tx - a.x);

    // Separation from teammate followers / soldiers within 0.7 m. Each
    // pushes us tangentially so we curve around the target rather than
    // stacking head-to-tail behind whoever got there first.
    const SEP_R = 0.7;
    const SEP_R2 = SEP_R * SEP_R;
    let sx = 0, sz = 0;
    for (let i = 0; i < this.ants.length; i++) {
      const o = this.ants[i];
      if (o === a || !o.alive) continue;
      if (o.team !== a.team) continue;
      if (o.role === 'player') continue;
      const odx = a.x - o.x;
      const odz = a.z - o.z;
      const d2 = odx * odx + odz * odz;
      if (d2 > 0 && d2 < SEP_R2) {
        const inv = 1 / d2;
        sx += odx * inv;
        sz += odz * inv;
      }
    }
    if (sx !== 0 || sz !== 0) {
      const sepHead = Math.atan2(sz, sx);
      // Blend hard — 0.45 is enough to redirect into a flank without
      // losing the target lock entirely.
      const blend = wrapAngle(sepHead - desired);
      desired = wrapAngle(desired + blend * 0.45);
    }
    return desired;
  }

  private stepForager(a: AntInternal, dt: number): void {
    if (a.pauseTimer > 0) {
      a.pauseTimer -= dt;
      a.vx *= Math.max(0, 1 - 6 * dt);
      a.vz *= Math.max(0, 1 - 6 * dt);
    } else {
      a.nextPauseIn -= dt;
      if (a.nextPauseIn <= 0) {
        a.pauseTimer = this.randRange(this.pauseMinDur, this.pauseMaxDur);
        a.nextPauseIn = this.randRange(this.pauseMinGap, this.pauseMaxGap);
      }
    }

    // Starving focus: when hunger > 0.7, force task='search' so the ant
    // actively seeks food. They still carry whatever they have, but behave
    // like searchers for pheromone/steering purposes.
    const starving = a.hunger > HUNGER_STARVING;
    if (starving) a.task = 'search';

    // Nest defence: if the team's alarm is active AND this ant is close to
    // home, drop whatever it's doing and charge the intruder. Overrides
    // search/return/carrying logic below — defenders don't care about food
    // while the colony is under attack.
    const alarm = this.nestAlarms[a.team];
    if (alarm.active) {
      const nest = this.nests[a.team];
      const ndx = a.x - nest.x;
      const ndz = a.z - nest.z;
      if (ndx * ndx + ndz * ndz < NEST_DEFENSE_RADIUS_SQ) {
        const dxE = alarm.x - a.x;
        const dzE = alarm.z - a.z;
        const desired = Math.atan2(dzE, dxE);
        a.targetHeading = desired;
        // Drop any carried food — defending is the priority.
        a.carrying = false;
        a.carryValue = 0;
        a.task = 'search';
        return;
      }
    }

    if (a.task === 'search') {
      this.steerSearch(a, dt);
      this.pheromones[a.team].deposit(a.x, a.z, 1, FORAGE_CONFIG.depositHomeTrail * dt);
      // Hungry ants head home even while searching — they'll still grab food
      // if they stumble on some along the way (pickup logic handles that).
      if (a.hunger > 0.5 && !a.carrying) {
        const nest = this.nests[a.team];
        const desired = Math.atan2(nest.z - a.z, nest.x - a.x);
        const delta = wrapAngle(desired - a.targetHeading);
        a.targetHeading = wrapAngle(a.targetHeading + delta * 3.0 * dt);
      }
    } else {
      this.steerReturn(a, dt);
      // Scouts blaze the initial trail — they deposit more food-pheromone per
      // step so workers have a strong scent to follow back to fresh food.
      const trailMul = a.casteType === 'scout' ? 2.5 : 1.0;
      this.pheromones[a.team].deposit(a.x, a.z, 0, FORAGE_CONFIG.depositFoodTrail * trailMul * dt);
    }

    // Enemy / ally-in-combat aggro — only while searching so carrying ants
    // commit to getting food home. Starving ants also skip aggro: they ONLY
    // focus on food.
    if (a.task === 'search' && !starving) {
      const enemy = this.pickAggroTarget(a);
      if (enemy) {
        const desired = Math.atan2(enemy.z - a.z, enemy.x - a.x);
        const delta = wrapAngle(desired - a.targetHeading);
        a.targetHeading = wrapAngle(a.targetHeading + delta * 4.0 * dt);
      }
    }

    // Grasshopper hunt — scouts sense far, workers close. Kill on bite range.
    if (!starving) {
      for (let gi = 0; gi < this.grasshopperTargets.length; gi++) {
        const gh = this.grasshopperTargets[gi];
        if (!gh.alive) continue;
        const gdx = gh.x - a.x;
        const gdz = gh.z - a.z;
        const gd2 = gdx * gdx + gdz * gdz;
        const senseR = a.casteType === 'scout' ? 10 : 3;
        if (gd2 < senseR * senseR) {
          const desired = Math.atan2(gdz, gdx);
          const delta = wrapAngle(desired - a.targetHeading);
          a.targetHeading = wrapAngle(a.targetHeading + delta * 4.0 * dt);
        }
        // Bite trigger: only once the ant is body-on-body (≤ 15 cm).
        if (gd2 < 0.0225 && this.onGrasshopperBite) {
          this.onGrasshopperBite(gh.x, gh.z);
          break;
        }
      }
    }

    // Spider aggro — mobbing pull. Starving ants skip this too.
    if (!starving) {
      for (let si = 0; si < this.spiderTargets.length; si++) {
        const sp = this.spiderTargets[si];
        if (!sp.alive) continue;
        const sdx = sp.x - a.x;
        const sdz = sp.z - a.z;
        const sd2 = sdx * sdx + sdz * sdz;
        if (sd2 < 25 && sd2 > 0.04) {
          const desired = Math.atan2(sdz, sdx);
          const delta = wrapAngle(desired - a.targetHeading);
          a.targetHeading = wrapAngle(a.targetHeading + delta * 2.5 * dt);
          break;
        }
      }
    }
  }

  private integrate(a: AntInternal, dt: number): void {
    // Heading slew toward target. Only foragers "stop and turn" on big deltas —
    // player + followers steer continuously so click-to-move feels snappy.
    const dh = wrapAngle(a.targetHeading - a.heading);
    const absDh = Math.abs(dh);
    const TURN_IN_PLACE = 0.22;
    const canTurnInPlace = a.role === 'normal';
    const turning = canTurnInPlace && absDh > TURN_IN_PLACE;
    const maxStep = this.headingSlew * dt * (turning ? 2.5 : 1.0);
    a.heading += absDh <= maxStep ? dh : Math.sign(dh) * maxStep;
    a.heading = wrapAngle(a.heading);

    // Hunger-driven speed boost: up to +60% at hunger=1.
    const hungerMul = 1 + a.hunger * HUNGER_SPEED_BOOST;
    // Temperature scaling: +40 % at 35 °C, -40 % at -5 °C. Clamped so cold
    // doesn't freeze them in place outright (they still try to move, and the
    // freeze tick in hungerTick handles actual lethality).
    const tempSpeedMul = Math.max(0.6, Math.min(1.4, 1 + (this.currentTemperature - 20) / 40));
    let cap = (a.role === 'player' ? this.maxSpeed * PLAYER_SPEED_MULTIPLIER : this.maxSpeed) * hungerMul * tempSpeedMul;
    // Caste speed profile (fastest → slowest): breeder > worker > scout >
    // soldier > nurse > queen. Queen never actually moves via integrate but
    // the multiplier is here for completeness.
    cap *= speedMultForCaste(a.casteType);
    if (a.role === 'follower') {
      const p = this.ants[this.playerId];
      const dx = p ? p.x - a.x : 0;
      const dz = p ? p.z - a.z : 0;
      const distToP = Math.hypot(dx, dz);
      // Followers are faster than the player so they catch up instead of
      // falling behind. Distant stragglers get an extra burst.
      cap = this.maxSpeed * (distToP > 2 ? 2.8 : 2.2) * hungerMul;
    }
    // Alarm-response boost: a defender within its own nest's defence radius
    // while the nest is raising alarm gets a 1.5× speed edge while charging.
    if (a.role === 'normal' || a.role === 'follower') {
      const alarm = this.nestAlarms[a.team];
      if (alarm && alarm.active) {
        const nest = this.nests[a.team];
        const ndx = a.x - nest.x;
        const ndz = a.z - nest.z;
        if (ndx * ndx + ndz * ndz < NEST_DEFENSE_RADIUS_SQ) cap *= 1.5;
      }
    }
    // No approach braking for the player — full sprint until arrival, then
    // the stepPlayer arrival-branch hard-stops vx/vz to 0.

    const canAccelerate =
      a.pauseTimer <= 0 && !(a.role === 'player' && !a.goal) && !turning;

    if (turning) {
      // Brake hard so the ant visibly stops to turn, then walks straight.
      const k = Math.max(0, 1 - 10 * dt);
      a.vx *= k;
      a.vz *= k;
    } else if (canAccelerate) {
      const tx = Math.cos(a.heading);
      const tz = Math.sin(a.heading);
      a.vx += tx * this.linearAccel * dt;
      a.vz += tz * this.linearAccel * dt;
      // Project velocity onto the heading axis — kills the sideways drift that
      // produced the circumferential arcs.
      const forwardSpeed = a.vx * tx + a.vz * tz;
      a.vx = tx * forwardSpeed;
      a.vz = tz * forwardSpeed;
    }

    const sp = Math.hypot(a.vx, a.vz);
    if (sp > cap) {
      const k = cap / sp;
      a.vx *= k;
      a.vz *= k;
    }

    a.x += a.vx * dt;
    a.z += a.vz * dt;
  }

  private applyTaskTransitions(a: AntInternal): void {
    // Only normal foragers pick up food / drop off at the nest.
    if (a.role !== 'normal') return;
    if (a.task === 'search') {
      // Pick up from the ant's NOSE, not its centre. With a tight
      // 0.18 m radius the worker has to actually have its face on
      // the pile — no grazing-from-0.6 m back-up visual. The old
      // grabRadius (0.6 m) let them snap food from a body-length
      // away and immediately reverse, which looked like they were
      // backing into the pile.
      const noseOff = 0.05;
      const noseX = a.x + Math.cos(a.heading) * noseOff;
      const noseZ = a.z + Math.sin(a.heading) * noseOff;
      const item = this.food.tryPickup(noseX, noseZ, 0.18);
      if (item !== undefined) {
        a.carrying = true;
        a.carryValue = item.value;
        a.task = 'return';
        // Hold a brief pause at the pile — the 'bite' one-shot reads
        // properly when the body isn't already peeling away. Heading
        // flip happens after the bite completes on the next heading
        // slew tick via targetHeading.
        a.vx = 0;
        a.vz = 0;
        a.targetHeading = wrapAngle(a.heading + Math.PI);
        // Picking up food counts as eating — hunger fully resets.
        a.hunger = 0;
        this.onEvent?.('pickup');
      }
    } else {
      const nest = this.nests[a.team];
      const ddx = a.x - nest.x;
      const ddz = a.z - nest.z;
      if (ddx * ddx + ddz * ddz <= nest.radius * nest.radius) {
        a.carrying = false;
        a.task = 'search';
        const dur = 1.6 + Math.random() * 1.0;
        a.nestCycleT = dur;
        a.nestCycleDur = dur;
        // Food delivered to the colony stockpile.
        this.onFoodDelivered?.(a.team, a.carryValue);
        a.carryValue = 0;
        // While at the nest, eat from the stockpile if we're hungry.
        if (a.hunger > 0.3 && this.onAntEat?.(a.team, 1)) a.hunger = 0;
      }
    }
    // Hungry searching ants that walk into the nest can also eat from stockpile.
    if (a.task === 'search' && !a.carrying && a.hunger > 0.3) {
      const nest = this.nests[a.team];
      const ddx = a.x - nest.x;
      const ddz = a.z - nest.z;
      if (ddx * ddx + ddz * ddz <= nest.radius * nest.radius) {
        if (this.onAntEat?.(a.team, 1)) {
          a.hunger = 0;
          const dur = 1.2 + Math.random() * 0.8;
          a.nestCycleT = dur;
          a.nestCycleDur = dur;
        }
      }
    }
  }

  /** Callback fired when an ant drops food at its nest. */
  onFoodDelivered: ((team: 0 | 1, amount: number) => void) | null = null;
  /** Callback invoked when a hungry ant tries to eat from the stockpile. Returns true if fed. */
  onAntEat: ((team: 0 | 1, cost: number) => boolean) | null = null;

  private applyBounds(a: AntInternal): void {
    // Square bound — clamp x and z independently to ±wanderRadius. The
    // old circular bound stopped ants well inside the square map, so a
    // nest in the corner of the world had a useless donut of unreachable
    // ground. Reflecting velocity along whichever wall was hit keeps
    // them turned around naturally.
    const half = this.wanderRadius;
    let bumped = false;
    if (a.x > half) {
      a.x = half;
      if (a.vx > 0) a.vx = -a.vx;
      bumped = true;
    } else if (a.x < -half) {
      a.x = -half;
      if (a.vx < 0) a.vx = -a.vx;
      bumped = true;
    }
    if (a.z > half) {
      a.z = half;
      if (a.vz > 0) a.vz = -a.vz;
      bumped = true;
    } else if (a.z < -half) {
      a.z = -half;
      if (a.vz < 0) a.vz = -a.vz;
      bumped = true;
    }
    if (bumped) {
      a.heading = Math.atan2(a.vz, a.vx);
      a.targetHeading = a.heading;
    }
  }

  private nearestEnemyInMelee(a: AntInternal): AntInternal | null {
    const ants = this.ants;
    let best: AntInternal | null = null;
    let bestD2 = MELEE_RADIUS_SQ * 2;
    for (let i = 0; i < ants.length; i++) {
      const o = ants[i];
      if (!o.alive || o.team === a.team || o === a) continue;
      const dx = o.x - a.x;
      const dz = o.z - a.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; best = o; }
    }
    return best;
  }

  private steerSearch(a: AntInternal, dt: number): void {
    const { senseDistance, senseAngle, followStrength } = FORAGE_CONFIG;
    const hC = a.heading;
    const hL = hC + senseAngle;
    const hR = hC - senseAngle;
    const cx = a.x + Math.cos(hC) * senseDistance;
    const cz = a.z + Math.sin(hC) * senseDistance;
    const lx = a.x + Math.cos(hL) * senseDistance;
    const lz = a.z + Math.sin(hL) * senseDistance;
    const rx = a.x + Math.cos(hR) * senseDistance;
    const rz = a.z + Math.sin(hR) * senseDistance;

    const ph = this.pheromones[a.team];
    const sC = ph.sample(cx, cz, 0);
    const sL = ph.sample(lx, lz, 0);
    const sR = ph.sample(rx, rz, 0);

    const best = Math.max(sC, sL, sR);
    if (best > TRAIL_THRESHOLD) {
      const bestHeading = best === sC ? hC : best === sL ? hL : hR;
      const total = sC + sL + sR;
      const relative = total > 0 ? best / total : 1;
      const delta = wrapAngle(bestHeading - a.targetHeading);
      a.targetHeading = wrapAngle(
        a.targetHeading + delta * followStrength * relative * dt,
      );
      return;
    }

    const noise = (Math.random() - Math.random()) * this.turnNoise * dt;
    a.targetHeading = wrapAngle(a.targetHeading + noise);
  }

  private steerReturn(a: AntInternal, dt: number): void {
    const nest = this.nests[a.team];
    const dx = nest.x - a.x;
    const dz = nest.z - a.z;
    let desired = Math.atan2(dz, dx);

    this.pheromones[a.team].gradient(a.x, a.z, 1, this.gradScratch);
    const gx = this.gradScratch[0];
    const gz = this.gradScratch[1];
    const gmag = Math.hypot(gx, gz);
    if (gmag > 1e-6) {
      const gHeading = Math.atan2(gz, gx);
      const blendDelta = wrapAngle(gHeading - desired);
      desired = wrapAngle(desired + blendDelta * HOME_GRADIENT_BLEND);
    }

    const delta = wrapAngle(desired - a.targetHeading);
    a.targetHeading = wrapAngle(
      a.targetHeading + delta * FORAGE_CONFIG.followStrength * dt,
    );
  }

  private randRange(lo: number, hi: number): number {
    return lo + Math.random() * (hi - lo);
  }
}

/**
 * Per-caste natural lifespan in seconds of elapsed game time. Converted from
 * the user-specified real-world durations at 1 in-game day = 10 min real
 * (DAY_LENGTH = 600 s) and 24 in-game days per year (Weather.ts):
 *
 *   Queen    6 months =  12 days =  2 h real = 7200 s
 *   Nurse    1 year   =  24 days =  4 h real = 14400 s
 *   Soldier  1 month  =   2 days = 20 min    = 1200 s
 *   Worker   2 weeks  =   1 day  = 10 min    = 600 s
 *   Scout    1 week   = 0.5 day  =  5 min    = 300 s
 *   Breeder  1 month  =   2 days = 20 min    = 1200 s
 */
function lifespanForCaste(c: AntCaste): number {
  switch (c) {
    case 'queen':   return 3600;
    case 'nurse':   return 7200;
    case 'soldier': return 600;
    case 'worker':  return 300;
    case 'scout':   return 150;
    case 'breeder': return 600;
  }
}

function hpForCaste(c: AntCaste): number {
  switch (c) {
    case 'queen': return 20;
    case 'soldier': return 1.8;
    case 'breeder': return 1.2;
    case 'nurse': return 0.9;
    case 'scout': return 0.9;
    default: return 1;
  }
}

function damageMultForCaste(c: AntCaste): number {
  switch (c) {
    case 'queen':   return 3.0;   // Maximum
    case 'nurse':   return 2.0;   // Higher
    case 'soldier': return 1.5;   // High
    case 'worker':  return 1.0;   // Medium (baseline)
    case 'scout':   return 0.7;   // Low
    case 'breeder': return 0.5;   // Lowest
  }
}

function speedMultForCaste(c: AntCaste): number {
  switch (c) {
    case 'breeder': return 1.6;   // Highest
    case 'worker':  return 1.3;   // High
    case 'scout':   return 1.0;   // Medium (baseline)
    case 'soldier': return 0.8;   // Slow
    case 'nurse':   return 0.6;   // Slower
    case 'queen':   return 0.4;   // Slowest
  }
}

function corpseFoodForCaste(c: AntCaste): { amount: number; value: number } {
  switch (c) {
    case 'queen':   return { amount: 4, value: 20 };
    case 'soldier': return { amount: 2, value: 3 };
    case 'breeder': return { amount: 1, value: 3 };
    case 'nurse':   return { amount: 3, value: 5 };
    default:        return { amount: 1, value: 1 }; // worker / scout
  }
}

function hungerRateForCaste(c: AntCaste): number {
  switch (c) {
    case 'queen': return 1 / 720;   // very slow, fed by nurses
    case 'nurse': return 1 / 360;
    case 'scout': return 1 / 200;   // runs around a lot
    case 'soldier': return 1 / 220;
    case 'breeder': return 1 / 400;
    default: return 1 / 240;         // worker — 4 minutes
  }
}

function wrapAngle(a: number): number {
  let x = a;
  while (x > Math.PI) x -= TAU;
  while (x <= -Math.PI) x += TAU;
  return x;
}
